import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import * as Tone from 'tone';
import { useToneAudio } from '../hooks/useToneAudio';
import { useSynthManager } from '../hooks/useSynthManager';
import { useScoreParser } from '../hooks/useScoreParser';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { NoteHighlighter } from '../utils/noteHighlighter';
import { AutoScroller } from '../utils/autoScroller';
import { MeasureHighlighter } from '../utils/measureHighlighter';
import { LoopUtils } from '../utils/loopUtils';
import { DeviceDetection } from '../utils/deviceDetection';
import { SettingsManager } from '../utils/settingsManager';
import { normalizeClosedScoreSATB } from '../utils/satbScoreNormalizer';
import ScoreDisplay from './ScoreDisplay';
import SongGrid from './SongGrid';
import OnScoreTipDialog from './OnScoreTipDialog';
import VideoPlayerModal from './VideoPlayerModal';
import FeatureSignInDialog from './FeatureSignInDialog';
import { normalizeVideos } from '../utils/songVideos';
import { logMusicFileView, logAudioPlay } from '../utils/activityLogger';
import { isPhoneDevice } from '../utils/phoneDevice';
import MobilePracticeShell from './mobile/MobilePracticeShell';
import DesktopPracticeShell from './desktop/DesktopPracticeShell';
import './MusicXMLPlayer.css';
import '../styles/settings-controls.css';
import '../styles/auth.css';

const MusicXMLPlayer = ({
  fileName,
  setFileName,
  osmdRef,
  partControls: parentPartControls,
  setPartControls: setParentPartControls,
  tipRefreshTrigger,
  onTipsUpdated,
  isInTipPlacementMode,
  setIsInTipPlacementMode
}) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  // Phone or desktop shell, decided once per mount: switching later would
  // remount ScoreDisplay and destroy the live OSMD instance
  const [isPhone] = useState(isPhoneDevice);
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState(() => t('messages.selectFile'));

  // Function to inject metadata title into MusicXML content
  const injectMetadataTitle = useCallback(async (xmlContent, filename) => {
    if (!filename) return xmlContent;

    try {
      // Fetch metadata for this file
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(
        `${API_BASE}/api/musicxml?operation=get-metadata&filename=${encodeURIComponent(filename)}`
      );

      if (response.ok) {
        const metadata = await response.json();

        // If we have a custom title, inject it into the MusicXML
        if (metadata.title) {
          // Parse XML to modify title elements
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');

          // Try to find movement-title first (for individual songs/movements)
          let titleElement = xmlDoc.querySelector('movement-title');

          if (titleElement) {
            titleElement.textContent = metadata.title;
          } else {
            // If no movement-title, try work-title (for larger works)
            titleElement = xmlDoc.querySelector('work-title');
            if (titleElement) {
              titleElement.textContent = metadata.title;
            } else {
              // If neither exists, create a movement-title in the score header
              const scorePartwise = xmlDoc.querySelector('score-partwise');
              if (scorePartwise) {
                // Find or create the identification section
                let identification = xmlDoc.querySelector('identification');
                if (!identification) {
                  identification = xmlDoc.createElement('identification');
                  scorePartwise.insertBefore(identification, scorePartwise.firstChild);
                }

                // Create movement-title element
                const movementTitle = xmlDoc.createElement('movement-title');
                movementTitle.textContent = metadata.title;
                scorePartwise.insertBefore(movementTitle, identification);
              }
            }
          }

          // Serialize back to string
          const serializer = new XMLSerializer();
          return serializer.serializeToString(xmlDoc);
        }
      }
    } catch (error) {
      console.error('Failed to inject metadata title:', error);
    }

    // Return original content if anything fails
    return xmlContent;
  }, []);
  const fileInputRef = useRef(null);
  const [currentFileContent, setCurrentFileContent] = useState(null);
  const [songVideos, setSongVideos] = useState([]);
  const [isInitialState, setIsInitialState] = useState(true);

  // Reference videos for the loaded song, from its .meta.json sidecar. A failed
  // fetch just means no videos section — nothing worth interrupting the user for.
  useEffect(() => {
    if (!fileName) {
      setSongVideos([]);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
        const response = await fetch(
          `${API_BASE}/api/musicxml?operation=get-metadata&filename=${encodeURIComponent(fileName)}`
        );
        const metadata = response.ok ? await response.json() : null;
        if (!cancelled) setSongVideos(normalizeVideos(metadata?.videos));
      } catch {
        if (!cancelled) setSongVideos([]);
      }
    })();

    return () => { cancelled = true; };
  }, [fileName]);
  const [zoom, setZoom] = useState(() => {
    // Set default zoom based on device type
    return DeviceDetection.isMobile() ? 0.5 : 1.0;
  });
  const [autoScroll, setAutoScroll] = useState(true);
  const [lookaheadBeats, setLookaheadBeats] = useState(1); // Default 1 beat lookahead
  const [selectedRange, setSelectedRange] = useState({ start: null, end: null });
  const [isLoopMode, setIsLoopMode] = useState(false);
  const [loopBoundaries, setLoopBoundaries] = useState(null);
  const [isSwingMode, setIsSwingMode] = useState(false);
  const [swingRatio, setSwingRatio] = useState(5/8); // Default swing ratio (5:3 = 62.5%)
  const [swingRatioSource, setSwingRatioSource] = useState('auto'); // 'auto', '2:1', '3:2', '5:3', 'custom'
  const [detectedSwingInfo, setDetectedSwingInfo] = useState(null); // Stores parsed swing info from file
  const [audioLatency, setAudioLatency] = useState(() => {
    // Load saved calibration from localStorage
    const saved = localStorage.getItem('musicxml-audio-latency');
    if (saved) return parseFloat(saved);
    
    // Default to 0 for accurate highlighting sync
    return 0;
  });
  const [hasLoadedFile, setHasLoadedFile] = useState(false);
  const [showScore, setShowScore] = useState(false);
  const [currentPresetName, setCurrentPresetName] = useState('');
  const [tipsVisible, setTipsVisible] = useState(true);
  const [tipAvailability, setTipAvailability] = useState({ hasTips: false, tipCount: 0 });
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, clickedTip: null, clickPosition: null });
  const [showCreateTipDialog, setShowCreateTipDialog] = useState(false);
  const [createTipPosition, setCreateTipPosition] = useState(null);
  const [showEditTipDialog, setShowEditTipDialog] = useState(false);
  const [editingTip, setEditingTip] = useState(null);
  const [selectedPart, setSelectedPart] = useState(null); // Track which part is "me"
  const [showMePartDialog, setShowMePartDialog] = useState(false);
  const [confidence, setConfidence] = useState(0); // Confidence slider value (0-100) - start at 0 for solo mode
  const [isMousePlayMode, setIsMousePlayMode] = useState(false);
  // Mouse Play option: hovering a note also plays every other voice sounding at that moment
  const [isWholeChordMode, setIsWholeChordMode] = useState(false);
  const [transposeSteps, setTransposeSteps] = useState(0); // Key transposition in half steps (-11..+11)
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isRecordingMp3, setIsRecordingMp3] = useState(false);
  const [isRenderingMp3, setIsRenderingMp3] = useState(false);
  const mp3RecorderRef = useRef(null);
  const mp3PlaybackStartedRef = useRef(false);
  const [showVideoTutorial, setShowVideoTutorial] = useState(false);
  const [tutorialVideoURL, setTutorialVideoURL] = useState('https://tinyurl.com/ffse3hht'); // Default fallback
  const [showSignInDialog, setShowSignInDialog] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [pendingPresetToLoad, setPendingPresetToLoad] = useState(null);

  // osmdRef is now passed as prop
  const noteHighlighterRef = useRef(null);
  const autoScrollerRef = useRef(null);
  const measureHighlighterRef = useRef(null);
  const scoreDisplayRef = useRef(null);
  const hasLoadedFileRef = useRef(false);
  const mePartPromptedForRef = useRef(null); // File we already asked "which part is me?" for
  const scheduledCursorEventIdsRef = useRef([]);
  const animationFrameRef = useRef(null);
  // Where the highlight is on the note timeline (seconds), for the lyrics
  // overlay; null once stopped
  const playheadListenersRef = useRef(new Set());
  const emitPlayhead = useCallback((time) => playheadListenersRef.current.forEach(listener => listener(time)), []);
  const addPlayheadListener = useCallback((listener) => {
    playheadListenersRef.current.add(listener);
    return () => playheadListenersRef.current.delete(listener);
  }, []);

  // Custom hooks
  const audioHook = useToneAudio();
  const synthManager = useSynthManager(audioHook);
  
  const {
    isPlaying,
    setIsPlaying,
    isPaused,
    setIsPaused,
    isCountingIn,
    volume,
    setVolume,
    partVolumes,
    setPartVolume,
    getPartVolume,
    instrument,
    setInstrument,
    playbackSpeed,
    setPlaybackSpeed,
    startPlayback,
    pausePlayback,
    stopPlayback,
    togglePartMute,
    updateGlobalSynthSettings,
    noteEventsRef,
    pendingDisposalsRef,
    velocity,
    setVelocity,
    setLoopMode,
    setNoteEvents,
    exitLoopMode,
    jumpToTime,
    setOSMDRef,
    partSynthsRef,
    applyLiveTempo,
    getTransportBakedTime,
    metronomeEnabled,
    setMetronomeEnabled,
    setMeter
  } = audioHook;

  const { syncAllPartSynths } = synthManager;
  
  const scoreParser = useScoreParser(synthManager, isSwingMode, swingRatio, velocity);

  const {
    partControls,
    setPartControls,
    createPartControls,
    preparePlayback,
    handlePartToggle,
    handlePartVisibilityToggle,
    handlePartVisibilityBatch,
    handleUpperVoiceToggle,
    handleLowerVoiceToggle,
    syncVisibilityWithUI,
    getOriginalBPM,
    applyDivisiFilteringToHighlighting,
    // Repeat functionality
    respectRepeats,
    setRespectRepeats,
    toggleRespectRepeats,
    // Swing detection
    detectSwing
  } = scoreParser;

  // Authentication check function
  const requireAuth = useCallback((action) => {
    if (!user) {
      setShowSignInDialog(true);
      return false;
    }
    return true;
  }, [user]);

  // Clean up filename for preset name
  const cleanupFilename = (filename) => {
    console.log('🔧 Cleanup: Input filename:', filename);
    // Remove file extension
    let cleaned = filename.replace(/\.(musicxml|xml)$/i, '');
    console.log('🔧 Cleanup: After extension removal:', cleaned);
    
    // Replace underscores and hyphens with spaces
    cleaned = cleaned.replace(/[_-]/g, ' ');
    console.log('🔧 Cleanup: After underscore/hyphen replacement:', cleaned);
    
    // Replace multiple spaces with single space
    cleaned = cleaned.replace(/\s+/g, ' ');
    
    // Capitalize first letter of each word
    cleaned = cleaned.replace(/\b\w/g, letter => letter.toUpperCase());
    console.log('🔧 Cleanup: After capitalization:', cleaned);
    
    // Trim whitespace
    cleaned = cleaned.trim();
    console.log('🔧 Cleanup: Final result:', cleaned);
    
    return cleaned;
  };

  // Toast notification helper
  const showToastNotification = useCallback((message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 3000); // Hide after 3 seconds
  }, []);

  // Check for existing preset before loading file
  const handleFileSelectionWithPresetCheck = useCallback(async (filename, loadFileFunction) => {
    console.log('🔧 Preset check: Checking for existing preset before loading file:', filename);
    
    if (!user || !SettingsManager.canSaveSettings(user)) {
      console.log('🔧 Preset check: No user or save permission, loading file directly');
      loadFileFunction();
      return;
    }

    try {
      const cleanedName = cleanupFilename(filename);
      
      console.log('🔧 Preset check: Looking for preset with display name:', cleanedName);
      const existingPresets = await SettingsManager.getPresetsForCurrentDevice(user.id);
      console.log('🔧 Preset check: All existing preset names:', Object.keys(existingPresets));
      const presetExists = Object.keys(existingPresets).includes(cleanedName);
      
      if (presetExists) {
        console.log('🔧 Preset check: Found existing preset, triggering preset load');
        // Don't load the file - instead trigger preset loading
        setPendingPresetToLoad(cleanedName);
      } else {
        console.log('🔧 Preset check: No existing preset, loading file and will create new preset');
        loadFileFunction();
      }
    } catch (error) {
      console.error('🔧 Preset check error:', error);
      // If preset check fails, just load the file normally
      loadFileFunction();
    }
  }, [user, cleanupFilename]);

  const updateCurrentNotes = useCallback(() => {
    if (!isPlaying || partControls.length === 0) return;
    
    // Baked-time frame position: stays aligned with note event times across live tempo changes
    const currentTime = getTransportBakedTime();
    // Compensate for audio system latency by delaying visual highlighting
    const syncedTime = Math.max(0, currentTime - audioLatency);
    
    if (loopBoundaries && isLoopMode) {
      // Handle looping playback with time wrapping
      // CRITICAL FIX: Use currentTime directly for modulo, then apply reduced latency compensation
      const rawLoopTime = currentTime % loopBoundaries.duration;
      // Use smaller latency compensation for loops to reach end notes
      const reducedLatency = Math.min(audioLatency, 0.1); // Cap at 100ms for loops
      const compensatedLoopTime = Math.max(0, rawLoopTime - reducedLatency);
      
      const originalTime = LoopUtils.loopTimeToOriginalTime(compensatedLoopTime, loopBoundaries.startTime);
      emitPlayhead(originalTime);

      if (noteHighlighterRef.current && scoreParser.allNotesWithTimingRef.current) {
        let activeNoteEvents = scoreParser.allNotesWithTimingRef.current.filter(noteEvent => 
          originalTime >= noteEvent.startTime && 
          originalTime < noteEvent.endTime && // Remove the +0.1 tolerance that was causing trails
          !noteEvent.isRest &&
          noteEvent.sourceNote &&
          noteEvent.startTime >= loopBoundaries.startTime &&
          noteEvent.startTime <= loopBoundaries.endTime + 0.01
        );
        
        // Don't filter notes for highlighting - let the highlighting system handle voice filtering internally
        // The highlighting system needs to see ALL chord notes to decide which noteheads to highlight
        
        // Debug: Check if any active notes are swung
        const swungNotes = activeNoteEvents.filter(note => note.isSwung);
        if (swungNotes.length > 0) {
          // console.log(`MusicXMLPlayer: Found ${swungNotes.length} swung notes in active events at time ${originalTime.toFixed(3)}`);
        }
        
        // Get lookahead notes if lookahead is enabled
        if (lookaheadBeats > 0) {
          // Convert beats to seconds using current BPM and playback speed
          const currentBPM = getOriginalBPM() * playbackSpeed;
          const beatDurationSeconds = 60 / currentBPM;
          const lookaheadTimeSeconds = lookaheadBeats * beatDurationSeconds;
          const lookaheadTime = originalTime + lookaheadTimeSeconds;
          let lookaheadNoteEvents = scoreParser.allNotesWithTimingRef.current.filter(noteEvent => 
            lookaheadTime >= noteEvent.startTime && 
            lookaheadTime < noteEvent.endTime && 
            !noteEvent.isRest &&
            noteEvent.sourceNote &&
            noteEvent.startTime >= loopBoundaries.startTime &&
            noteEvent.startTime <= loopBoundaries.endTime + 0.01
          );
          
          // Update highlights with both current and lookahead notes
          noteHighlighterRef.current.updateHighlightsWithLookahead(activeNoteEvents, lookaheadNoteEvents);
        } else {
          // No lookahead - use original method
          noteHighlighterRef.current.updateHighlightsFromActiveNotes(activeNoteEvents);
        }
        
        
        if (autoScroll && autoScrollerRef.current) {
          // Use lookahead highlights for autoscroll if lookahead is enabled, otherwise use current highlights
          const highlightedNoteIds = lookaheadBeats > 0 
            ? noteHighlighterRef.current.getLookaheadHighlights()
            : noteHighlighterRef.current.getCurrentHighlights();
          autoScrollerRef.current.scrollToNotes(highlightedNoteIds);
        }
      }
    } else if (loopBoundaries && !isLoopMode) {
      // Handle single selection playback (no looping, no time wrapping)
      // Map the transformed event time back to original timeline for visual highlighting
      const originalTime = LoopUtils.loopTimeToOriginalTime(syncedTime, loopBoundaries.startTime);
      emitPlayhead(originalTime);

      if (noteHighlighterRef.current && scoreParser.allNotesWithTimingRef.current) {
        let activeNoteEvents = scoreParser.allNotesWithTimingRef.current.filter(noteEvent => 
          originalTime >= noteEvent.startTime && 
          originalTime < noteEvent.endTime && 
          !noteEvent.isRest &&
          noteEvent.sourceNote &&
          noteEvent.startTime >= loopBoundaries.startTime &&
          noteEvent.startTime <= loopBoundaries.endTime + 0.01
        );
        
        // Don't filter notes for highlighting - let the highlighting system handle voice filtering internally
        // The highlighting system needs to see ALL chord notes to decide which noteheads to highlight
        
        // Get lookahead notes if lookahead is enabled
        if (lookaheadBeats > 0) {
          // Convert beats to seconds using current BPM and playback speed
          const currentBPM = getOriginalBPM() * playbackSpeed;
          const beatDurationSeconds = 60 / currentBPM;
          const lookaheadTimeSeconds = lookaheadBeats * beatDurationSeconds;
          const lookaheadTime = originalTime + lookaheadTimeSeconds;
          let lookaheadNoteEvents = scoreParser.allNotesWithTimingRef.current.filter(noteEvent => 
            lookaheadTime >= noteEvent.startTime && 
            lookaheadTime < noteEvent.endTime && 
            !noteEvent.isRest &&
            noteEvent.sourceNote &&
            noteEvent.startTime >= loopBoundaries.startTime &&
            noteEvent.startTime <= loopBoundaries.endTime + 0.01
          );
          
          // Update highlights with both current and lookahead notes
          noteHighlighterRef.current.updateHighlightsWithLookahead(activeNoteEvents, lookaheadNoteEvents);
        } else {
          // No lookahead - use original method
          noteHighlighterRef.current.updateHighlightsFromActiveNotes(activeNoteEvents);
        }
        
        
        if (autoScroll && autoScrollerRef.current) {
          // Use lookahead highlights for autoscroll if lookahead is enabled, otherwise use current highlights
          const highlightedNoteIds = lookaheadBeats > 0 
            ? noteHighlighterRef.current.getLookaheadHighlights()
            : noteHighlighterRef.current.getCurrentHighlights();
          autoScrollerRef.current.scrollToNotes(highlightedNoteIds);
        }
      }
    } else {
      emitPlayhead(syncedTime);
      if (noteHighlighterRef.current && scoreParser.allNotesWithTimingRef.current) {
        let activeNoteEvents = scoreParser.allNotesWithTimingRef.current.filter(noteEvent =>
          syncedTime >= noteEvent.startTime &&
          syncedTime < noteEvent.endTime && 
          !noteEvent.isRest &&
          noteEvent.sourceNote
        );
        
        // Don't filter notes for highlighting - let the highlighting system handle voice filtering internally
        // The highlighting system needs to see ALL chord notes to decide which noteheads to highlight
        
        // Get lookahead notes if lookahead is enabled
        if (lookaheadBeats > 0) {
          // Convert beats to seconds using current BPM and playback speed
          const currentBPM = getOriginalBPM() * playbackSpeed;
          const beatDurationSeconds = 60 / currentBPM;
          const lookaheadTimeSeconds = lookaheadBeats * beatDurationSeconds;
          const lookaheadTime = syncedTime + lookaheadTimeSeconds;
          let lookaheadNoteEvents = scoreParser.allNotesWithTimingRef.current.filter(noteEvent => 
            lookaheadTime >= noteEvent.startTime && 
            lookaheadTime < noteEvent.endTime && 
            !noteEvent.isRest &&
            noteEvent.sourceNote
          );
          
          // Update highlights with both current and lookahead notes
          noteHighlighterRef.current.updateHighlightsWithLookahead(activeNoteEvents, lookaheadNoteEvents);
        } else {
          // No lookahead - use original method
          noteHighlighterRef.current.updateHighlightsFromActiveNotes(activeNoteEvents);
        }
        
        
        if (autoScroll && autoScrollerRef.current) {
          // Use lookahead highlights for autoscroll if lookahead is enabled, otherwise use current highlights
          const highlightedNoteIds = lookaheadBeats > 0 
            ? noteHighlighterRef.current.getLookaheadHighlights()
            : noteHighlighterRef.current.getCurrentHighlights();
          autoScrollerRef.current.scrollToNotes(highlightedNoteIds);
        }
      }
    }
  }, [isPlaying, partControls.length, isLoopMode, loopBoundaries, autoScroll, audioLatency, applyDivisiFilteringToHighlighting, partControls, lookaheadBeats, getOriginalBPM, playbackSpeed, getTransportBakedTime, emitPlayhead])

  // Timer-based update loop to prevent excessive re-renders
  useEffect(() => {
    let updateInterval = null;

    if (isPlaying) {
      // More frequent updates on mobile for smoother highlighting
      const updateFrequency = DeviceDetection.isMobile() ? 33 : 50; // 30fps vs 20fps
      
      updateInterval = setInterval(() => {
        // Check both React state AND Transport state for resilience
        const transportIsPlaying = Tone.Transport.state === 'started';
        const shouldHighlight = (isPlaying || transportIsPlaying) && partControls.length > 0;
        
        if (shouldHighlight) {
          updateCurrentNotes();
        }
      }, updateFrequency);
    } else {
      // Clear highlights when not playing
      if (!isPaused) {
        emitPlayhead(null);
        if (noteHighlighterRef.current) {
          noteHighlighterRef.current.clearAllHighlights();
        }
        scheduledCursorEventIdsRef.current.forEach(id => Tone.Transport.clear(id));
        scheduledCursorEventIdsRef.current = [];
        if (osmdRef.current?.cursor) {
          osmdRef.current.cursor.reset();
          osmdRef.current.cursor.hide();
        }
        if (autoScrollerRef.current) {
          autoScrollerRef.current.reset();
        }
      }
    }

    return () => {
      if (updateInterval) {
        clearInterval(updateInterval);
      }
    };
  }, [isPlaying, isPaused, updateCurrentNotes, partControls.length, osmdRef, emitPlayhead]);

  // Update status on language change if still in initial state
  // Safari mobile fix: Prevent part visibility reset during language changes
  useEffect(() => {
    if (isInitialState) {
      setStatus(t('messages.selectFile'));
    }
  }, [t, isInitialState]);

  // Handle pending preset loading
  useEffect(() => {
    if (pendingPresetToLoad) {
      console.log('🔧 Loading pending preset:', pendingPresetToLoad);
      handleLoadRecentPreset(pendingPresetToLoad);
      setPendingPresetToLoad(null);
    }
  }, [pendingPresetToLoad]);

  // Handle OSMD initialization
  const handleOsmdReady = useCallback((osmd) => {
    osmdRef.current = osmd;
    
    setOSMDRef(osmdRef); // Pass osmdRef to audioHook for divisi tracking
    noteHighlighterRef.current = new NoteHighlighter(osmd);
    
    // Set audio references for highlighting colors
    noteHighlighterRef.current.setAudioReferences(getPartVolume, audioHook.mutedPartsRef);
    
    // Set part control references for divisi highlighting
    noteHighlighterRef.current.setPartControlReferences(
      () => scoreParser.partControlsRef.current, 
      scoreParser.partToInstrumentMapRef
    );
    
    // Initialize auto-scroller with better error handling
    setTimeout(() => {
      if (scoreDisplayRef.current) {
        const container = scoreDisplayRef.current.getContainer();
        if (container) {
          autoScrollerRef.current = new AutoScroller(container, osmd);
          // Set initial lookahead value
          autoScrollerRef.current.setLookaheadBeats(lookaheadBeats);
        }
      }
    }, 100); // Small delay to ensure DOM is ready
    
    if (osmdRef.current?.cursor) {
        osmdRef.current.cursor.hide();
    }
  }, [getPartVolume, audioHook.mutedPartsRef]);

  // Helper function to get key signature for note spelling
  const getKeySignature = useCallback((sourceNote) => {
    let keyInstructions = null;
    
    if (sourceNote.SourceMeasure?.KeyInstructions && sourceNote.SourceMeasure.KeyInstructions.length > 0) {
      keyInstructions = sourceNote.SourceMeasure.KeyInstructions[sourceNote.SourceMeasure.KeyInstructions.length - 1];
    }
    
    if (!keyInstructions && sourceNote.ParentStaff?.KeyInstructions && sourceNote.ParentStaff.KeyInstructions.length > 0) {
      keyInstructions = sourceNote.ParentStaff.KeyInstructions[sourceNote.ParentStaff.KeyInstructions.length - 1];
    }
    
    if (!keyInstructions && sourceNote.parentStaffEntry?.parentStaff?.KeyInstructions && sourceNote.parentStaffEntry.parentStaff.KeyInstructions.length > 0) {
      keyInstructions = sourceNote.parentStaffEntry.parentStaff.KeyInstructions[sourceNote.parentStaffEntry.parentStaff.KeyInstructions.length - 1];
    }
    
    return keyInstructions;
  }, []);

  // Helper function for key signature-aware note spelling
  const getCorrectNoteSpelling = useCallback((chromaticIndex, keyInstructions) => {
    const defaultNotes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    
    if (!keyInstructions) {
      return defaultNotes[chromaticIndex];
    }
    
    const keySignatureCount = keyInstructions.KeyTypeOriginal || keyInstructions.Key || 0;
    const isSharpKey = keySignatureCount > 0;
    const isFlatKey = keySignatureCount < 0;
    const numAccidentals = Math.abs(keySignatureCount);
    
    if (isFlatKey) {
      const flatNotes = ['C', 'Db', 'D', 'Eb', 'E', 'F', 'Gb', 'G', 'Ab', 'A', 'Bb', 'B'];
      const flatOrder = [10, 3, 8, 1, 6, 11, 4];
      const affectedFlats = flatOrder.slice(0, numAccidentals);
      
      if (affectedFlats.includes(chromaticIndex)) {
        return flatNotes[chromaticIndex];
      }
    } else if (isSharpKey) {
      const sharpNotes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
      const sharpOrder = [6, 1, 8, 3, 10, 4, 11];
      const affectedSharps = sharpOrder.slice(0, numAccidentals);
      
      if (affectedSharps.includes(chromaticIndex)) {
        return sharpNotes[chromaticIndex];
      }
    }
    
    const naturalNotes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    return naturalNotes[chromaticIndex];
  }, []);

  // Check if current time signature allows swing (only /4 and /2 time signatures)
  // First time signature in the loaded MusicXML, or null if none is found.
  // Shared by swing (which needs the beat unit) and the metronome (which
  // needs beats per measure for the count-off).
  const getTimeSignature = useCallback(() => {
    const xmlContent = window._currentMusicXMLContent;
    if (typeof xmlContent !== 'string') return null;
    const match = xmlContent.match(/<time[^>]*>[\s\S]*?<beats>(\d+)<\/beats>[\s\S]*?<beat-type>(\d+)<\/beat-type>[\s\S]*?<\/time>/i);
    if (match) {
      return { beats: parseInt(match[1], 10), beatType: parseInt(match[2], 10) };
    }
    // Fall back to what OSMD parsed, for files without an explicit <time>
    const active = osmdRef.current?.Sheet?.SourceMeasures?.[0]?.ActiveTimeSignature;
    if (active && active.Numerator && active.Denominator) {
      return { beats: active.Numerator, beatType: active.Denominator };
    }
    return null;
  }, []);

  const getTimeSignatureAllowsSwing = useCallback(() => {
    if (!osmdRef.current) return false;
    
    try {
      const timeSignature = getTimeSignature();
      if (timeSignature) {
        // Only allow swing for /4 and /2 time signatures
        return timeSignature.beatType === 4 || timeSignature.beatType === 2;
      }
      
      // Fallback to OSMD duration method (less reliable)
      const osmd = osmdRef.current;
      if (osmd.cursor?.Iterator?.CurrentMeasure?.Duration?.RealValue !== undefined) {
        const duration = osmd.cursor.Iterator.CurrentMeasure.Duration.RealValue;
        
        // Only allow common /4 and /2 signatures for fallback
        // 4/4 = 1.0, 2/4 = 0.5, 2/2 = 1.0
        // NOTE: 0.75 could be 3/4 (swing OK) OR 6/8 (swing NOT OK), so we can't reliably detect from duration alone
        return Math.abs(duration - 1.0) < 0.01 || Math.abs(duration - 0.5) < 0.01;
      }
      
      return false;
    } catch (error) {
      return false;
    }
  }, [getTimeSignature]);

  // Turn an OSMD source note into a Tone.js pitch string, honouring the
  // key signature spelling and any active transposition
  const sourceNoteToPitch = useCallback((sourceNote) => {
    if (sourceNote?.halfTone === undefined) return null;
    const keyInstructions = getKeySignature(sourceNote);
    const adjustedHalfTone = sourceNote.halfTone + (osmdRef.current?.Sheet?.Transpose || 0);
    const noteName = getCorrectNoteSpelling(adjustedHalfTone % 12, keyInstructions);
    return noteName + Math.floor(adjustedHalfTone / 12).toString();
  }, [getKeySignature, getCorrectNoteSpelling]);

  // Every note in the hovered note's measure (all parts and voices) that is
  // sounding at the moment the hovered note starts: it began at or before that
  // point and has not yet ended. Tied continuations are separate notes in OSMD,
  // so a note held over from earlier still shows up here. Hidden and muted
  // parts are included on purpose: the point is to hear the complete chord.
  const getSimultaneousNotes = useCallback((sourceNote) => {
    const measure = sourceNote?.SourceMeasure;
    const onset = sourceNote?.ParentVoiceEntry?.Timestamp?.RealValue;
    if (!measure || onset === undefined) return [sourceNote];

    const notes = [];
    const seen = new Set();
    (measure.VerticalSourceStaffEntryContainers || []).forEach(container => {
      const start = container?.Timestamp?.RealValue;
      if (start === undefined || start > onset + 1e-6) return;
      (container.StaffEntries || []).forEach(staffEntry => {
        (staffEntry?.VoiceEntries || []).forEach(voiceEntry => {
          (voiceEntry?.Notes || []).forEach(note => {
            if (!note || note.isRestFlag || note.halfTone === undefined) return;
            const partId = note.ParentStaff?.ParentInstrument?.Id;
            const length = note.Length?.RealValue || 0;
            const isSounding = note === sourceNote || start + length > onset + 1e-6;
            if (!isSounding) return;
            // One trigger per pitch per part; unison doublings add nothing
            const key = `${partId}_${note.halfTone}`;
            if (seen.has(key)) return;
            seen.add(key);
            notes.push(note);
          });
        });
      });
    });
    return notes.length > 0 ? notes : [sourceNote];
  }, []);

  // Function to play individual notes in MousePlay mode
  const playClickedNote = useCallback(async (sourceNote, staffIndex, measureNumber) => {
    if (!sourceNote || !partSynthsRef.current) {
      return;
    }

    try {
      // Ensure Tone.js audio context is running
      if (Tone.context.state !== 'running') {
        await Tone.start();
      }

      const notesToPlay = isWholeChordMode ? getSimultaneousNotes(sourceNote) : [sourceNote];
      const availableSynths = Object.values(partSynthsRef.current);
      const fallbackSynth = availableSynths[staffIndex] || availableSynths[0];

      // Group pitches by synth so each part plays its own notes with its own sound
      const bySynth = new Map();
      notesToPlay.forEach(note => {
        const pitch = sourceNoteToPitch(note);
        if (!pitch) return;
        const partId = note.ParentStaff?.ParentInstrument?.Id;
        const synth = (partId !== undefined && partSynthsRef.current[partId]) || fallbackSynth;
        if (!synth || synth._disposed || synth.disposed === true) return;
        if (!bySynth.has(synth)) bySynth.set(synth, new Set());
        bySynth.get(synth).add(pitch);
      });

      const duration = 0.5; // 500ms duration
      const noteVelocity = 1.0; // Maximum velocity for MousePlay mode

      bySynth.forEach((pitchSet, synth) => {
        // Temporarily boost volume for MousePlay, then restore after the note
        const originalVolume = synth.volume ? synth.volume.value : null;
        if (synth.volume) {
          synth.volume.value = Math.max(originalVolume, -6); // Ensure at least -6dB volume
        }

        const pitches = [...pitchSet];
        synth.triggerAttackRelease(pitches.length === 1 ? pitches[0] : pitches, duration, '+0', noteVelocity);

        if (originalVolume !== null && synth.volume) {
          setTimeout(() => {
            if (synth.volume && !synth._disposed) {
              synth.volume.value = originalVolume;
            }
          }, 600); // Restore after note duration
        }
      });
    } catch (error) {
      console.error('Error playing clicked note:', error);
    }
  }, [partSynthsRef, isWholeChordMode, getSimultaneousNotes, sourceNoteToPitch]);

  // Function to exit MousePlay mode (for ESC key)
  const exitMousePlayMode = useCallback(() => {
    setIsMousePlayMode(false);
  }, []);

  // Outside hover-to-hear, while stopped or counting in, my first note (the
  // highlighted section's, if any) still plays when hovered, for a starting
  // pitch
  const cueFromMeasure = selectedRange.start;
  const scoreCue = useMemo(() => (
    !isPhone && !isMousePlayMode && ((!isPlaying && !isPaused) || isCountingIn)
      ? { part: selectedPart ?? null, fromMeasure: cueFromMeasure ?? null }
      : null
  ), [isPhone, isMousePlayMode, isPlaying, isPaused, isCountingIn, selectedPart, cueFromMeasure]);

  // Update AutoScroller lookahead when lookaheadBeats changes
  useEffect(() => {
    if (autoScrollerRef.current) {
      autoScrollerRef.current.setLookaheadBeats(lookaheadBeats);
    }
  }, [lookaheadBeats]);

  // Update MeasureHighlighter when MousePlay mode changes
  useEffect(() => {
    if (measureHighlighterRef.current) {
      // Update the MousePlay mode function reference
      measureHighlighterRef.current.isMousePlayMode = () => isMousePlayMode;
      
      // Clear measure selection when MousePlay is activated
      if (isMousePlayMode && (selectedRange.start !== null || selectedRange.end !== null)) {
        measureHighlighterRef.current.clearAllHighlights();
        setSelectedRange({ start: null, end: null });
      }
    }
  }, [isMousePlayMode, selectedRange]);

  // Comprehensive cleanup function
  const performFullCleanup = useCallback(() => {
    // Stop playback for real before tearing the song down. Disposing synths
    // and cancelling events is not enough: the Transport would keep running
    // from its old position (and looping), so the next song's events would be
    // scheduled in the past, and the highlighters, cursor, auto-scroller and
    // loop flags would still think a song is playing.
    audioHook.stopPlayback();
    if (measureHighlighterRef.current) {
      // The highlighter owns the selection and a poll copies it into React
      // state, so it must forget the range too or the old one comes back.
      measureHighlighterRef.current.clearAllHighlights();
    }
    scheduledCursorEventIdsRef.current.forEach(id => Tone.Transport.clear(id));
    scheduledCursorEventIdsRef.current = [];
    if (noteHighlighterRef.current) {
      noteHighlighterRef.current.clearAllHighlights();
    }
    if (autoScrollerRef.current) {
      autoScrollerRef.current.reset();
    }
    audioHook.exitLoopMode();

    // Clear loading states
    setIsLoading(false);
    
    // Clear file states
    setFileName(null);
    setCurrentFileContent(null);
    setHasLoadedFile(false);
    hasLoadedFileRef.current = false;
    setShowScore(false);
    
    // Clear preset state
    setCurrentPresetName('');
    
    // Clear playback states
    setIsPlaying(false);
    setIsPaused(false);
    setIsLoopMode(false);
    setLoopBoundaries(null);
    setSelectedRange({ start: null, end: null });
    
    // Clear audio and UI states
    audioHook.disposeAllSynths();
    audioHook.clearScheduledEvents();
    scoreParser.setPartControls([]);
    
    // Clear cursors and highlights
    if (osmdRef.current?.cursor) {
      osmdRef.current.cursor.reset();
      osmdRef.current.cursor.hide();
    }
    
    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    // Reset to initial state
    setIsInitialState(true);
    setStatus(() => t('messages.selectFile'));
  }, [audioHook, scoreParser, t]);


  // Fetch tutorial video URL setting
  useEffect(() => {
    const fetchTutorialSetting = async () => {
      try {
        const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
        const response = await fetch(`${API_BASE}/api/settings?category=tutorial&public_only=true`);
        
        if (response.ok) {
          const data = await response.json();
          const videoURL = data.settings?.tutorial_video_url;
          if (videoURL && videoURL.trim()) {
            setTutorialVideoURL(videoURL);
          }
        }
      } catch (error) {
        // Keep default fallback URL
      }
    };

    fetchTutorialSetting();
  }, []);

  // Context menu handlers
  const handleContextMenu = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    
    // Check if we clicked on a tip or tip-related element
    const clickedElement = event.target;
    let clickedTip = null;
    let tipId = null;
    
    // First try to get tip ID from the clicked element itself
    tipId = clickedElement.getAttribute('data-tip-id');
    
    // If not found, try to find the closest tip pin
    if (!tipId) {
      const tipPin = clickedElement.closest('.tip-pin');
      if (tipPin) {
        tipId = tipPin.getAttribute('data-tip-id');
      }
    }
    
    // Also try to find any parent element with a tip ID
    if (!tipId) {
      const tipElement = clickedElement.closest('[data-tip-id]');
      if (tipElement) {
        tipId = tipElement.getAttribute('data-tip-id');
      }
    }
    
    
    if (tipId) {
      clickedTip = { id: tipId };
    }
    
    // Get click position relative to score container for creating new tips
    let clickPosition = null;
    if (!clickedTip && scoreDisplayRef.current) {
      const scoreContainer = scoreDisplayRef.current.getContainer();
      if (scoreContainer) {
        const containerRect = scoreContainer.getBoundingClientRect();
        clickPosition = {
          x: event.clientX - containerRect.left,
          y: event.clientY - containerRect.top,
          screenX: event.clientX,
          screenY: event.clientY
        };
      }
    }
    
    setContextMenu({
      visible: true,
      x: event.clientX,
      y: event.clientY,
      clickedTip,
      clickPosition
    });
  }, []);

  const hideContextMenu = useCallback(() => {
    setContextMenu({ visible: false, x: 0, y: 0, clickedTip: null, clickPosition: null });
  }, []);

  const handleToggleTips = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    
    setTipsVisible(!tipsVisible);
    hideContextMenu();
  }, [tipsVisible, hideContextMenu]);

  const handleTipsLoaded = useCallback((tipData) => {
    setTipAvailability(tipData);
  }, []);

  // The measure highlighter decides whether a tap selects a bar or moves the
  // playhead. Its flag is owned here, in one place: playing or paused means
  // taps move the playhead; stopped means taps select.
  useEffect(() => {
    if (measureHighlighterRef.current) {
      measureHighlighterRef.current.setPlayingState(isPlaying || isPaused);
    }
  }, [isPlaying, isPaused]);

  const handleCreateTip = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (contextMenu.clickPosition) {
      const position = {
        pinX: contextMenu.clickPosition.x,
        pinY: contextMenu.clickPosition.y
      };
      setCreateTipPosition(position);
      setShowCreateTipDialog(true);
    }
    hideContextMenu();
  }, [contextMenu.clickPosition, hideContextMenu]);

  const handleDeleteTip = useCallback(async (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (contextMenu.clickedTip?.id && user?.id && window.confirm('Are you sure you want to delete this tip?')) {
      try {
        const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
        const url = `${API_BASE}/api/tips?operation=deleteTip&id=${contextMenu.clickedTip.id}&userId=${user.id}`;
        
        const response = await fetch(url, { method: 'DELETE' });
        
        if (response.ok) {
          // console.log('Tip deleted successfully');
          onTipsUpdated();
        } else {
          const errorData = await response.json();
          console.error('Failed to delete tip:', errorData.error);
          alert('Failed to delete tip: ' + (errorData.error || 'Unknown error'));
        }
      } catch (error) {
        console.error('Error deleting tip:', error);
        alert('Error deleting tip: ' + error.message);
      }
    }
    hideContextMenu();
  }, [contextMenu.clickedTip, user?.id, hideContextMenu]);

  const handleCreateTipSubmit = useCallback(async (tipData) => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/tips?operation=createTip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(tipData)
      });
      
      if (response.ok) {
        // console.log('Tip created successfully');
        setShowCreateTipDialog(false);
        setCreateTipPosition(null);
        onTipsUpdated();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create tip');
      }
    } catch (error) {
      console.error('Error creating tip:', error);
      throw error; // Re-throw so CreateTipDialog can handle it
    }
  }, []);

  const handleCreateTipClose = useCallback(() => {
    setShowCreateTipDialog(false);
    setCreateTipPosition(null);
  }, []);

  const handleEditTip = useCallback(async (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (contextMenu.clickedTip?.id && user?.id) {
      try {
        // Fetch the full tip data for editing
        const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
        const response = await fetch(
          `${API_BASE}/api/tips?operation=getTips&userId=${user.id}&type=on-score&musicxmlId=${fileName}`
        );
        
        if (response.ok) {
          const tips = await response.json();
          const tipToEdit = tips.find(tip => tip.id.toString() === contextMenu.clickedTip.id.toString());
          
          if (tipToEdit) {
            setEditingTip(tipToEdit);
            setShowEditTipDialog(true);
          } else {
            alert('Tip not found or you do not have permission to edit it.');
          }
        } else {
          const errorData = await response.json();
          console.error('Failed to load tip for editing:', errorData.error);
          alert('Failed to load tip for editing: ' + (errorData.error || 'Unknown error'));
        }
      } catch (error) {
        console.error('Error loading tip for editing:', error);
        alert('Error loading tip for editing: ' + error.message);
      }
    }
    hideContextMenu();
  }, [contextMenu.clickedTip, user?.id, fileName, hideContextMenu]);

  const handleEditTipSubmit = useCallback(async (tipData) => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/tips?operation=updateTip&id=${editingTip.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(tipData)
      });
      
      if (response.ok) {
        console.log('Tip updated successfully');
        setShowEditTipDialog(false);
        setEditingTip(null);
        onTipsUpdated();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update tip');
      }
    } catch (error) {
      console.error('Error updating tip:', error);
      throw error; // Re-throw so OnScoreTipDialog can handle it
    }
  }, [editingTip]);

  const handleEditTipClose = useCallback(() => {
    setShowEditTipDialog(false);
    setEditingTip(null);
  }, []);

  // File upload handler
  const handleFileUpload = async (event) => {
    console.log('🔧 File upload: Handler called');
    const file = event.target.files[0];
    if (file) {
      console.log('🔧 File upload: File selected:', file.name);
      console.log('🔧 File upload: User:', user ? 'signed in' : 'not signed in');
      console.log('🔧 File upload: Can save settings:', user ? SettingsManager.canSaveSettings(user) : 'N/A');
      
      // Check authentication before allowing file upload
      if (!requireAuth('file_upload')) {
        console.log('🔧 File upload: Auth failed, returning');
        // Reset the file input to clear the selection
        if (event.target) {
          event.target.value = '';
        }
        return;
      }
      // Perform comprehensive cleanup before loading new file
      performFullCleanup();
      
      setStatus(t('messages.loading'));
      setIsLoading(true);
      setIsInitialState(false);

      // Reset playback speed when loading new file
      setPlaybackSpeed(1.0);

      // Reset swing mode when loading new file
      setIsSwingMode(false);

      // Reset transposition when loading new file (fresh sheet starts untransposed)
      setTransposeSteps(0);

      // Clear previous MusicXML content
      window._currentMusicXMLContent = null;

      // Clear any pending disposals
      pendingDisposalsRef.current.forEach(timeout => clearTimeout(timeout));
      pendingDisposalsRef.current = [];

      // Clear note highlighting when loading new file
      if (noteHighlighterRef.current) {
        noteHighlighterRef.current.clearAllHighlights();
      }

      // Clear measure highlighting when loading new file
      if (measureHighlighterRef.current) {
        measureHighlighterRef.current.clearAllHighlights();
      }

      // Clear any previously scheduled cursor events
      scheduledCursorEventIdsRef.current.forEach(id => Tone.Transport.clear(id));
      scheduledCursorEventIdsRef.current = [];
      if (osmdRef.current?.cursor) {
        osmdRef.current.cursor.reset();
        osmdRef.current.cursor.hide();
      }

      // Reset everything
      audioHook.disposeAllSynths();
      audioHook.clearScheduledEvents();
      scoreParser.setPartControls([]);

      try {
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const originalContent = normalizeClosedScoreSATB(e.target.result).content;
            const contentWithMetadata = await injectMetadataTitle(originalContent, file.name);

            setCurrentFileContent(originalContent); // Store original content for settings
            window._currentMusicXMLContent = originalContent; // Make available globally for repeat analysis
            await osmdRef.current.load(contentWithMetadata);

            // Set zoom before rendering to ensure mobile gets correct initial size
            if (osmdRef.current.zoom !== zoom) {
              osmdRef.current.zoom = zoom;
            }

            // Make container visible BEFORE rendering so OSMD can calculate proper dimensions
            // This fixes the blank canvas issue on initial load
            setHasLoadedFile(true);
            hasLoadedFileRef.current = true;

            // Wait for React to update the DOM and browser to layout the container
            // Use multiple frames and a setTimeout to ensure layout is complete on slower devices
            await new Promise(resolve => {
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  setTimeout(resolve, 50);
                });
              });
            });

            await osmdRef.current.render();

            // Force OSMD to recalculate dimensions by triggering a resize event
            // This ensures correct rendering even if initial dimensions were wrong
            window.dispatchEvent(new Event('resize'));

            // Initialize measure highlighter after rendering
            setTimeout(() => {
              if (scoreDisplayRef.current) {
                const container = scoreDisplayRef.current.getContainer();
                if (container) {
                  if (measureHighlighterRef.current) measureHighlighterRef.current.destroy();
                  measureHighlighterRef.current = new MeasureHighlighter(
                    osmdRef.current, 
                    container,
                    (newRange) => {
                      setSelectedRange(newRange);
                    },
                    handleMeasureJump,
                    () => isMousePlayMode,
                    playClickedNote,
                    exitMousePlayMode
                  );
                  // Initialize with current part visibility state
                  measureHighlighterRef.current.updatePartVisibility(partControls);
                }
              }
            }, 300); // Wait for render to complete

            if (!osmdRef.current.cursor || !osmdRef.current.Sheet || !osmdRef.current.Sheet.Instruments) {
              setStatus(t('messages.errorFileInit', { filename: file.name }));
              setIsLoading(false);
              return;
            }

            createPartControls(osmdRef.current);

            if (!osmdRef.current.Sheet.Instruments || osmdRef.current.Sheet.Instruments.length === 0) {
              setStatus(t('messages.errorNoInstruments', { filename: file.name }));
              setIsLoading(false);
              return;
            }

            updateGlobalSynthSettings();
            setFileName(file.name);
            setStatus(`Loaded: ${file.name}. ${t('messages.preparing')}...`);

            await preparePlayback(osmdRef.current, audioHook);

            // Auto-detect swing from MusicXML tags
            const swingInfo = detectSwing(osmdRef.current);
            setDetectedSwingInfo(swingInfo);
            if (swingInfo.hasSwing && !isSwingMode) {
              const timeSignatureAllowsSwing = getTimeSignatureAllowsSwing();
              if (timeSignatureAllowsSwing) {
                setIsSwingMode(true);
                // If file has a ratio and we're in auto mode, use it
                if (swingInfo.swingRatio && swingRatioSource === 'auto') {
                  setSwingRatio(swingInfo.swingRatio);
                }
                // Re-run preparePlayback with swing enabled
                await preparePlayback(osmdRef.current, audioHook);
              }
            }

            if (noteEventsRef.current.length > 0) {
              const originalBPM = getOriginalBPM();
              setStatus(t('messages.ready', { filename: file.name, bpm: originalBPM }));
              hasLoadedFileRef.current = true;
              setHasLoadedFile(true);
              setShowScore(true);
              
              // Log music file loading activity
              logMusicFileView(user, file.name, 'musicxml');
              
              // Wait for part controls to be fully initialized before creating preset
              setTimeout(async () => {
                console.log('🔧 File upload: Creating auto-preset after part initialization');
                // Create new preset since no existing preset was found (checked before file load)
                if (user && SettingsManager.canSaveSettings(user)) {
                  try {
                    console.log('🔧 Auto-preset: Creating new preset for file:', file.name);
                    const cleanedName = cleanupFilename(file.name);
                    
                    // Initialize all parts to default settings before saving preset
                    const currentPartControls = scoreParser.partControlsRef.current || [];
                    console.log('🔧 Auto-preset: Initializing', currentPartControls.length, 'parts to defaults');
                    
                    // Reset all parts to defaults
                    const defaultPartControls = currentPartControls.map(part => ({
                      ...part,
                      checked: true,
                      visible: true,
                      upperVoice: true,
                      lowerVoice: true
                    }));
                    setPartControls(defaultPartControls);
                    
                    // Set all part volumes to 100%
                    currentPartControls.forEach(part => {
                      setPartVolume(part.id, 100);
                    });
                    
                    // Small delay to ensure state updates are applied
                    await new Promise(resolve => setTimeout(resolve, 100));
                    
                    const currentPlayerState = {
                      playbackSpeed: 1.0, // Default speed
                      volume: 0, // Default master volume
                      instrument,
                      audioLatency,
                      partControls: defaultPartControls,
                      partVolumes: Object.fromEntries(currentPartControls.map(part => [part.id, 100])), // All volumes at 100%
                      zoom: 1.0, // Default zoom
                      autoScroll: true, // Default auto-scroll
                      lookaheadBeats: 0, // Default lookahead
                      fileName: file.name,
                      hasLoadedFile: true,
                      selectedPart: null, // Default selected part
                      confidence: 80, // Default confidence
                      isSwingMode: false, // Default swing mode
                      swingRatio: 5/8, // Default swing ratio
                      swingRatioSource: 'auto', // Default swing ratio source
                      transposeSteps: 0 // Default transposition
                    };
                    
                    const currentSettings = SettingsManager.collectCurrentSettings(
                      currentPlayerState,
                      e.target.result, // The file content that was just loaded
                      file.name
                    );
                    
                    await SettingsManager.savePreset(
                      cleanedName,
                      currentSettings,
                      user.id
                    );
                    
                    setCurrentPresetName(cleanedName);
                    console.log('🔧 Auto-preset: Created with default settings for', currentPartControls.length, 'parts');
                    showToastNotification(`✓ ${t('messages.savedAs', { preset: cleanedName })}`);
                  } catch (error) {
                    console.error('🔧 Auto-preset error:', error);
                    showToastNotification(`✗ Auto-preset failed: ${error.message}`);
                  }
                } else {
                  console.log('🔧 Auto-preset: Skipped - user not signed in or no save permission');
                }
              }, 200); // Wait 200ms for part controls to be fully initialized
            } else {
              setStatus(t('messages.noPlayableNotes', { filename: file.name }));
            }
          } catch (error) {
            setStatus(t('messages.errorProcessing', { error: error.message }));
          } finally {
            setIsLoading(false);
          }
        };
        reader.readAsText(file);
      } catch (error) {
        setStatus(t('messages.errorReading', { error: error.message }));
        setIsLoading(false);
      }
    }
  };

  // Handle cloud file loading
  const handleCloudFileLoad = async (content, filename) => {
    // Check authentication before allowing cloud file load
    if (!requireAuth('cloud_file_load')) {
      return;
    }
    
    performFullCleanup();
    
    setStatus(t('messages.loading'));
    setIsLoading(true);
    setIsInitialState(false);

    // Reset playback speed when loading new file
    setPlaybackSpeed(1.0);

    // Reset swing mode when loading new file
    setIsSwingMode(false);

    // Reset transposition when loading new file (fresh sheet starts untransposed)
    setTransposeSteps(0);

    // Clear previous MusicXML content
    window._currentMusicXMLContent = null;

    // Clear any pending disposals
    pendingDisposalsRef.current.forEach(timeout => clearTimeout(timeout));
    pendingDisposalsRef.current = [];

    // Clear note highlighting when loading new file
    if (noteHighlighterRef.current) {
      noteHighlighterRef.current.clearAllHighlights();
    }

    try {
      content = normalizeClosedScoreSATB(content).content;
      const contentWithMetadata = await injectMetadataTitle(content, filename);

      setCurrentFileContent(content); // Store original content
      window._currentMusicXMLContent = content; // Make available globally for repeat analysis
      await osmdRef.current.load(contentWithMetadata);

      // Set zoom before rendering to ensure mobile gets correct initial size
      if (osmdRef.current.zoom !== zoom) {
        osmdRef.current.zoom = zoom;
      }

      // Make container visible BEFORE rendering so OSMD can calculate proper dimensions
      // This fixes the blank canvas issue on initial load
      setHasLoadedFile(true);
      hasLoadedFileRef.current = true;

      // Wait for React to update the DOM and browser to layout the container
      // Use multiple frames and a setTimeout to ensure layout is complete on slower devices
      await new Promise(resolve => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTimeout(resolve, 50);
          });
        });
      });

      await osmdRef.current.render();

      // Force OSMD to recalculate dimensions by triggering a resize event
      // This ensures correct rendering even if initial dimensions were wrong
      window.dispatchEvent(new Event('resize'));

      // Initialize measure highlighter after rendering
      setTimeout(() => {
        if (scoreDisplayRef.current) {
          const container = scoreDisplayRef.current.getContainer();
          if (container) {
            if (measureHighlighterRef.current) measureHighlighterRef.current.destroy();
            measureHighlighterRef.current = new MeasureHighlighter(
              osmdRef.current,
              container,
              (newRange) => {
                setSelectedRange(newRange);
              },
              handleMeasureJump,
              () => isMousePlayMode,
              playClickedNote,
              exitMousePlayMode
            );
            // Initialize with current part visibility state
            measureHighlighterRef.current.updatePartVisibility(partControls);
          }
        }
      }, 300);

      if (!osmdRef.current.cursor || !osmdRef.current.Sheet || !osmdRef.current.Sheet.Instruments) {
        setStatus(t('messages.errorFileInit', { filename }));
        setIsLoading(false);
        return;
      }

      createPartControls(osmdRef.current);

      if (!osmdRef.current.Sheet.Instruments || osmdRef.current.Sheet.Instruments.length === 0) {
        setStatus(t('messages.errorNoInstruments', { filename }));
        setIsLoading(false);
        return;
      }

      updateGlobalSynthSettings();
      setFileName(filename);
      setStatus(`Loaded: ${filename}. ${t('messages.preparing')}...`);

      await preparePlayback(osmdRef.current, audioHook);

      // Auto-detect swing from MusicXML tags
      const swingInfo = detectSwing(osmdRef.current);
      setDetectedSwingInfo(swingInfo);
      if (swingInfo.hasSwing && !isSwingMode) {
        const timeSignatureAllowsSwing = getTimeSignatureAllowsSwing();
        if (timeSignatureAllowsSwing) {
          setIsSwingMode(true);
          // If file has a ratio and we're in auto mode, use it
          if (swingInfo.swingRatio && swingRatioSource === 'auto') {
            setSwingRatio(swingInfo.swingRatio);
          }
          // Re-run preparePlayback with swing enabled
          await preparePlayback(osmdRef.current, audioHook);
        }
      }

      if (noteEventsRef.current.length > 0) {
        const originalBPM = getOriginalBPM();
        setStatus(t('messages.ready', { filename: filename, bpm: originalBPM }));
        hasLoadedFileRef.current = true;
        setHasLoadedFile(true);
        setShowScore(true);

        // Log music file loading activity
        logMusicFileView(user, filename, 'musicxml');

        // Wait for part controls to be fully initialized before creating preset
        setTimeout(async () => {
          console.log('🔧 Cloud file: Creating auto-preset after part initialization');
          // Create new preset since no existing preset was found (checked before file load)
          if (user && SettingsManager.canSaveSettings(user)) {
            try {
              console.log('🔧 Auto-preset (cloud): Creating new preset for file:', filename);
              const cleanedName = cleanupFilename(filename);
              console.log('🔧 Auto-preset (cloud): Will save with display name:', cleanedName);
              
              // Initialize all parts to default settings before saving preset
              const currentPartControls = scoreParser.partControlsRef.current || [];
              console.log('🔧 Auto-preset (cloud): Initializing', currentPartControls.length, 'parts to defaults');
              
              // Reset all parts to defaults
              const defaultPartControls = currentPartControls.map(part => ({
                ...part,
                checked: true,
                visible: true,
                upperVoice: true,
                lowerVoice: true
              }));
              setPartControls(defaultPartControls);
              
              // Set all part volumes to 100%
              currentPartControls.forEach(part => {
                setPartVolume(part.id, 100);
              });
              
              // Small delay to ensure state updates are applied
              await new Promise(resolve => setTimeout(resolve, 100));
              
              const currentPlayerState = {
                playbackSpeed: 1.0, // Default speed
                volume: 0, // Default master volume
                instrument,
                audioLatency,
                partControls: defaultPartControls,
                partVolumes: Object.fromEntries(currentPartControls.map(part => [part.id, 100])), // All volumes at 100%
                zoom: 1.0, // Default zoom
                autoScroll: true, // Default auto-scroll
                lookaheadBeats: 0, // Default lookahead
                fileName: filename,
                hasLoadedFile: true,
                selectedPart: null, // Default selected part
                confidence: 80, // Default confidence
                isSwingMode: false, // Default swing mode
                swingRatio: 5/8, // Default swing ratio
                swingRatioSource: 'auto', // Default swing ratio source
                transposeSteps: 0 // Default transposition
              };
              
              const currentSettings = SettingsManager.collectCurrentSettings(
                currentPlayerState,
                content, // The file content that was just loaded
                filename
              );
              
              await SettingsManager.savePreset(
                cleanedName,
                currentSettings,
                user.id
              );
              
              setCurrentPresetName(cleanedName);
              console.log('🔧 Auto-preset (cloud): Created with default settings for', currentPartControls.length, 'parts');
              showToastNotification(`✓ ${t('messages.savedAs', { preset: cleanedName })}`);
            } catch (error) {
              console.error('🔧 Auto-preset (cloud) error:', error);
              showToastNotification(`✗ Auto-preset failed: ${error.message}`);
            }
          } else {
            console.log('🔧 Auto-preset (cloud): Skipped - user not signed in or no save permission');
          }
        }, 200); // Wait 200ms for part controls to be fully initialized
      } else {
        setStatus(t('messages.noPlayableNotes', { filename: filename }));
      }
    } catch (error) {
      setStatus(t('messages.errorProcessing', { error: error.message }));
    } finally {
      setIsLoading(false);
    }
  };

  // Load a specific XML file by filename from cloud storage for tip editing
  const handleLoadFileRequest = async (filename) => {
    try {
      // First get the list of files to find the one with matching filename
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const listResponse = await fetch(`${API_BASE}/api/musicxml?operation=list`);
      const listData = await listResponse.json();
      const targetFile = listData.files?.find(file => file.filename === filename);
      
      if (!targetFile) {
        throw new Error(t('tips.fileNotFound', { filename }));
      }
      
      // Download the file content
      const downloadResponse = await fetch(`${API_BASE}/api/musicxml?operation=download&url=${encodeURIComponent(targetFile.url)}`);
      
      if (!downloadResponse.ok) {
        throw new Error(t('tips.failedToDownloadFile', { filename }));
      }
      
      const content = await downloadResponse.text();
      
      // Load the file using the existing cloud file loading function
      await handleCloudFileLoad(content, filename);
      
    } catch (error) {
      console.error('Failed to load file for tip editing:', error);
      throw error; // Re-throw to let the caller handle the error display
    }
  };

  // Play selection once or enter loop mode
  const handlePlaySelection = useCallback((shouldLoop = false) => {
    if (!selectedRange || selectedRange.start === null) {
      setStatus(t('messages.selectMeasure'));
      return;
    }

    // For single measure selection, set end = start
    const loopRange = {
      start: selectedRange.start,
      end: selectedRange.end !== null ? selectedRange.end : selectedRange.start
    };


    // Extract timing boundaries
    // CRITICAL FIX: When respect repeats is enabled, use EXPANDED notes to get correct timing
    // for measures after repeats/endings. Otherwise, use original notes.
    // The expanded notes have playbackIndex which allows us to find the LAST occurrence
    // of each measure, ensuring timing is calculated from the final position in the score.
    const notesToUse = respectRepeats
      ? scoreParser.allNotesWithTimingRef.current
      : scoreParser.originalNotesWithTimingRef.current;

    const boundaries = LoopUtils.extractTimingBoundaries(
      loopRange,
      notesToUse,
      osmdRef.current
    );

    if (!boundaries) {
      setStatus(t('messages.errorTimingCalculation'));
      return;
    }

    // Create loop-compatible events
    // Use expanded events when respect repeats is enabled, original events otherwise
    const eventsToUse = respectRepeats
      ? noteEventsRef.current
      : scoreParser.originalNoteEventsRef.current;

    const transformedEvents = LoopUtils.createLoopNoteEvents(
      [], // We'll use the direct event transformation
      eventsToUse,
      boundaries
    );

    // Only set up audio loop mode if we want looping
    if (shouldLoop) {
      setLoopMode(transformedEvents, boundaries.duration);
    } else {
      // For single playback, use the transformed events but don't activate looping
      // The transformed events have timing adjusted to start from 0
      setNoteEvents(transformedEvents);
      exitLoopMode(); // Ensure loop mode is off
      
      // Schedule a stop event after the selection duration
      const stopTime = boundaries.duration + 0.1; // Small buffer
      setTimeout(() => {
        if (!isLoopMode && Tone.Transport.state === "started") {
          stopPlayback();
        }
      }, stopTime * 1000);
    }
    
    // Always set boundaries for visual highlighting (both loop and single mode)
    setLoopBoundaries(boundaries);

    setIsLoopMode(shouldLoop); // Only set loop mode if user wants looping
    
    
    if (shouldLoop) {
      setStatus(`Loop mode: Measures ${boundaries.startMeasure}-${boundaries.endMeasure} (${boundaries.duration.toFixed(2)}s)`);
    } else {
      setStatus(`Playing selection: Measures ${boundaries.startMeasure}-${boundaries.endMeasure}`);
    }
    
    return { boundaries, transformedEvents };
  }, [selectedRange, scoreParser.originalNotesWithTimingRef, scoreParser.originalNoteEventsRef, setLoopMode, setNoteEvents, exitLoopMode, t]);

  // Playback handlers
  const handlePlay = useCallback(async (playOptionsOrEvent) => {
    // Button onClick passes the event; only treat plain objects with our keys as options
    const playOptions = playOptionsOrEvent && typeof playOptionsOrEvent.countOff === 'boolean' ? playOptionsOrEvent : {};
    // Check authentication before allowing playback
    if (!requireAuth('play')) {
      return;
    }

    // Give the metronome the meter, plus the length of any pickup bar when
    // playback starts at the top of the score, so its clicks and accents sit
    // on the real beats rather than counting from the first note.
    const timeSignature = getTimeSignature();
    if (timeSignature) {
      const firstMeasure = osmdRef.current?.Sheet?.SourceMeasures?.[0];
      const startsAtTop = !isPaused && (!selectedRange || selectedRange.start === null || selectedRange.start === firstMeasure?.MeasureNumber);
      const nominalWholeNotes = timeSignature.beats / timeSignature.beatType;
      const actualWholeNotes = firstMeasure?.Duration?.RealValue;
      const hasPickup = Number.isFinite(actualWholeNotes) && actualWholeNotes > 0 && actualWholeNotes < nominalWholeNotes - 1e-6;
      setMeter({
        beats: timeSignature.beats,
        beatType: timeSignature.beatType,
        pickupQuarters: startsAtTop && hasPickup ? (nominalWholeNotes - actualWholeNotes) * 4 : 0
      });
    }
    
    // iOS Chrome fallback - allow play even without note events
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (noteEventsRef.current.length === 0 && !isPaused) {
      if (isMobile) {
        // iOS Chrome Fix: Don't try to call preparePlayback if OSMD has no sheet loaded
        // Just rely on the existing note events that should have been created during file load
        console.log('iOS Chrome: Note events available:', noteEventsRef.current.length);
        console.log('iOS Chrome: OSMD Sheet exists:', !!osmdRef.current?.Sheet);
        
        // If we have a loaded sheet but no note events, there might be a timing issue
        if (osmdRef.current?.Sheet && noteEventsRef.current.length === 0) {
          console.log('iOS Chrome: Sheet exists but no note events, allowing play anyway for mobile');
          // For mobile, we'll allow the play to proceed and let startPlayback handle it
        }
      }
      
      // iOS Chrome Fix: Allow play to continue if we have a loaded sheet even without note events
      if (noteEventsRef.current.length === 0) {
        if (isMobile && osmdRef.current?.Sheet) {
          console.log('iOS Chrome: Proceeding with play despite no note events');
          // Allow play to continue for mobile with loaded sheet
        } else {
          setStatus(t('messages.noNotes'));
          return;
        }
      }
    }

    if (!isPaused) {
      if (osmdRef.current?.cursor) {
        osmdRef.current.cursor.reset();
        osmdRef.current.cursor.hide();
      }

      scheduledCursorEventIdsRef.current.forEach(id => Tone.Transport.clear(id));
      scheduledCursorEventIdsRef.current = [];

      // Initialize autoscroller when starting from beginning
      if (autoScrollerRef.current) {
        autoScrollerRef.current.initialize(autoScroll && !(selectedRange && selectedRange.start !== null));
      }

      // Check if user has made a selection
      if (selectedRange && selectedRange.start !== null) {
        const result = handlePlaySelection(isLoopMode); // Use current loop mode state
        if (result) {
          await startPlayback(playOptions);
          
          // Log audio playback activity for selection
          if (fileName) {
            logAudioPlay(user, fileName, {
              fileType: 'musicxml',
              playbackSpeed,
              bpm: Math.round(getOriginalBPM() * playbackSpeed),
              hasSelection: true,
              isLoopMode: isLoopMode,
              selectedMeasures: `${result.boundaries.startMeasure}-${result.boundaries.endMeasure}`,
              selectionDuration: result.boundaries.duration
            });
          }
          
          
          const currentBPM = Math.round(getOriginalBPM() * playbackSpeed);
          if (isLoopMode) {
            setStatus(`Looping: Measures ${result.boundaries.startMeasure}-${result.boundaries.endMeasure} at ${currentBPM} BPM...`);
          } else {
            setStatus(`Playing selection: Measures ${result.boundaries.startMeasure}-${result.boundaries.endMeasure} at ${currentBPM} BPM...`);
          }
        }
        return;
      }
    }

    await startPlayback(playOptions);
    
    // Log audio playback activity
    if (fileName) {
      logAudioPlay(user, fileName, {
        fileType: 'musicxml',
        playbackSpeed,
        bpm: Math.round(getOriginalBPM() * playbackSpeed),
        hasSelection: selectedRange && selectedRange.start !== null,
        isLoopMode: isLoopMode,
        selectedMeasures: selectedRange ? `${selectedRange.start}-${selectedRange.end || selectedRange.start}` : null
      });
    }
    
    
    const currentBPM = Math.round(getOriginalBPM() * playbackSpeed);
    if (selectedRange && selectedRange.start !== null && !isLoopMode) {
      const endMeasure = selectedRange.end || selectedRange.start;
      setStatus(`Playing measures ${selectedRange.start}-${endMeasure} at ${currentBPM} BPM...`);
    } else {
      setStatus(t('messages.playing', { bpm: currentBPM }));
    }
  }, [noteEventsRef, isPaused, startPlayback, osmdRef, setStatus, getOriginalBPM, playbackSpeed, selectedRange, isLoopMode, handlePlaySelection, t, user, fileName, audioHook, scoreParser, currentFileContent, getTimeSignature, setMeter, autoScroll]);

  const handleMeasureJump = useCallback((measureNumber) => {
    // Check the MeasureHighlighter's playing state (more reliable than React state)
    const highlighterIsPlaying = measureHighlighterRef.current?.isPlaying;
    
    // Check if we actually have notes available 
    if (!scoreParser.allNotesWithTimingRef.current) {
      // console.log('❌ Cannot jump - no notes available');
      return;
    }

    // Calculate start time for the measure
    const measureStartTime = measureHighlighterRef.current?.getMeasureStartTime(
      measureNumber, 
      scoreParser.allNotesWithTimingRef.current
    );

    if (measureStartTime !== undefined) {
      // Pass the correct playing state to jumpToTime
      jumpToTime(measureStartTime, highlighterIsPlaying);
      setStatus(t('messages.jumpedToMeasure', { measureNumber }));
    } else {
    }
  }, [jumpToTime, setStatus, scoreParser.allNotesWithTimingRef]);

  const handlePause = useCallback(() => {
    pausePlayback();
    
    
    setStatus(t('messages.paused'));
  }, [pausePlayback, setStatus, t]);

  const handleStop = useCallback(async () => {
    // First stop playback which handles Transport and synth cleanup
    await stopPlayback();
    

    // Clear any remaining scheduled events
    scheduledCursorEventIdsRef.current.forEach(id => Tone.Transport.clear(id));
    scheduledCursorEventIdsRef.current = [];

    // Reset cursor
    if (osmdRef.current?.cursor) {
      osmdRef.current.cursor.reset();
      osmdRef.current.cursor.hide();
    }

    // Clear highlighting immediately
    if (noteHighlighterRef.current) {
      noteHighlighterRef.current.clearAllHighlights();
    }

    // Reset autoscroller
    if (autoScrollerRef.current) {
      autoScrollerRef.current.reset();
    }

    // Exit active loop but keep checkbox state
    exitLoopMode();
    setLoopBoundaries(null);

    // Force clear any pending intervals/timeouts
    setTimeout(() => {
      setStatus(t('messages.stopped'));
    }, 10); // Small delay to ensure cleanup completes
  }, [stopPlayback, osmdRef, setStatus, isLoopMode, exitLoopMode, t]);

  // A reference video and the score playing at once is just noise, so starting
  // a video pauses playback (position is kept, unlike a full stop)
  const handleVideoStart = useCallback(() => {
    if (isPlaying) {
      handlePause();
    }
  }, [isPlaying, handlePause]);

  const handleClearSelection = useCallback(() => {
    if (!measureHighlighterRef.current) return;

    measureHighlighterRef.current.clearAllHighlights();

    // Leave loop mode completely. Only telling the audio hook used to leave
    // isLoopMode/loopBoundaries set, so playback ran normally while the note
    // highlighter kept using the old loop's modulo timing and went dark.
    exitLoopMode();
    setIsLoopMode(false);
    setLoopBoundaries(null);

    setSelectedRange({ start: null, end: null });
  }, [measureHighlighterRef, exitLoopMode]);

  const handlePartToggleWrapper = useCallback((instrumentId, checked) => {
    const associatedParts = handlePartToggle(instrumentId, checked);

    associatedParts.forEach(partId => {
      togglePartMute(partId, !checked);
    });
  }, [handlePartToggle, togglePartMute]);

  // New wrapper for visibility toggle
  const handlePartVisibilityToggleWrapper = useCallback((instrumentId, visible) => {
    const renderCoordinator = scoreDisplayRef.current?.getRenderCoordinator?.();
    handlePartVisibilityToggle(instrumentId, visible, measureHighlighterRef, noteHighlighterRef, renderCoordinator);
  }, [handlePartVisibilityToggle]);

  // Handler for repeat toggle
  const handleToggleRespectRepeats = useCallback(async (checked) => {
    await toggleRespectRepeats(audioHook);
  }, [toggleRespectRepeats, audioHook]);

  // New wrapper for batch visibility updates to prevent race conditions
  const handlePartVisibilityBatchWrapper = useCallback((updates) => {
    const renderCoordinator = scoreDisplayRef.current?.getRenderCoordinator?.();
    handlePartVisibilityBatch(updates, measureHighlighterRef, noteHighlighterRef, renderCoordinator);
  }, [handlePartVisibilityBatch]);

  // Handler for selected part change
  const handleSelectedPartChange = useCallback((partId) => {
    setSelectedPart(partId);
  }, []);

  // Ask which part is "me" the first time a song loads without one.
  // The delay lets a loading preset restore its saved selectedPart first;
  // if that happens the effect re-runs and cancels the pending prompt.
  useEffect(() => {
    if (!hasLoadedFile || isLoading || !fileName) return;
    if (selectedPart !== null && selectedPart !== undefined) return;
    if (partControls.length < 2) return; // Nothing to choose in single-part scores
    if (mePartPromptedForRef.current === fileName) return;

    const timer = setTimeout(() => {
      mePartPromptedForRef.current = fileName;
      setShowMePartDialog(true);
    }, 800);
    return () => clearTimeout(timer);
  }, [hasLoadedFile, isLoading, fileName, selectedPart, partControls]);

  const handleMePartDialogSelect = useCallback((partId) => {
    setShowMePartDialog(false);
    setSelectedPart(partId);
  }, []);

  // Auto-apply confidence when override is enabled and selected part changes
  useEffect(() => {
    if (audioHook.overrideVolumesEnabled && selectedPart !== null && partControls.length > 0) {
      const partIds = partControls.map(p => p.id);
      audioHook.applyConfidenceToOverrides(partIds, selectedPart, confidence);
    }
  }, [audioHook.overrideVolumesEnabled, selectedPart]);

  // Wrapper for syncVisibilityWithUI
  const syncVisibilityWithUIWrapper = useCallback(() => {
    const renderCoordinator = scoreDisplayRef.current?.getRenderCoordinator?.();
    syncVisibilityWithUI(measureHighlighterRef, noteHighlighterRef, renderCoordinator);
  }, [syncVisibilityWithUI]);

  // For backward compatibility
  const handleEnterLoopMode = useCallback(() => {
    return handlePlaySelection(true);
  }, [handlePlaySelection]);

  // Transpose the piece by a number of half steps (notation + audio)
  const handleTransposeChange = useCallback((newSteps) => {
    const clamped = Math.max(-11, Math.min(11, newSteps));
    setTransposeSteps(clamped);

    const osmd = osmdRef.current;
    if (!osmd?.Sheet) return;

    // Already applied (e.g., retried during preset load) - nothing to do
    if ((osmd.Sheet.Transpose || 0) === clamped) return;

    try {
      // OSMD's TransposeCalculator respells notes and key signatures for display
      osmd.Sheet.Transpose = clamped;
      osmd.updateGraphic();

      const renderCoordinator = scoreDisplayRef.current?.getRenderCoordinator?.();
      if (renderCoordinator) {
        renderCoordinator.requestRender({ priority: 'immediate', reason: 'transpose-change' });
      } else {
        osmd.render();
      }

      // Re-schedule audio events at the new pitch (mirrors the swing toggle flow)
      if (hasLoadedFile && scoreParser.allNotesWithTimingRef.current.length > 0) {
        preparePlayback(osmd, audioHook);
        if (isLoopMode && loopBoundaries) {
          handleEnterLoopMode();
        }
      }
    } catch (error) {
      console.error('Transpose error:', error);
    }
  }, [osmdRef, hasLoadedFile, scoreParser.allNotesWithTimingRef, preparePlayback, audioHook, isLoopMode, loopBoundaries, handleEnterLoopMode]);

  // Export the score as a paginated PDF (respects transposition and part visibility)
  const handleExportPdf = useCallback(async () => {
    if (!hasLoadedFile || isGeneratingPdf) return;

    setIsGeneratingPdf(true);
    showToastNotification(t('messages.preparingPdf') || 'Preparing PDF…');
    try {
      const { exportScoreToPDF } = await import('../utils/pdfExporter');
      await exportScoreToPDF({
        xmlContent: currentFileContent || window._currentMusicXMLContent,
        fileName,
        transposeSteps,
        partControls
      });
    } catch (error) {
      console.error('PDF export error:', error);
      showToastNotification(t('messages.pdfFailed') || '✗ PDF export failed');
    } finally {
      setIsGeneratingPdf(false);
    }
  }, [hasLoadedFile, isGeneratingPdf, currentFileContent, fileName, transposeSteps, partControls, showToastNotification, t]);

  // Shared tail of both MP3 export paths: encode PCM and download the file
  const encodeAndDownloadMp3 = useCallback(async (pcm) => {
    showToastNotification(t('messages.encodingMp3') || 'Encoding MP3…');
    const { encodeMp3ToBlob, downloadBlob } = await import('../utils/mp3Recorder');
    const blob = await encodeMp3ToBlob(pcm);

    const baseName = (fileName || 'score').replace(/\.(musicxml|xml|mxl)$/i, '');
    const transposeSuffix = transposeSteps ? ` (${transposeSteps > 0 ? '+' : ''}${transposeSteps})` : '';
    downloadBlob(blob, `${baseName}${transposeSuffix}.mp3`);
    showToastNotification(t('messages.mp3Ready') || '✓ MP3 downloaded');
  }, [fileName, transposeSteps, showToastNotification, t]);

  // Export an MP3 with the current settings. Fast path renders the prepared
  // note events offline (seconds instead of the piece's duration); if that
  // fails for any reason we fall back to recording one real-time playback.
  const handleExportMp3 = useCallback(async () => {
    if (!hasLoadedFile || isRecordingMp3 || isRenderingMp3 || isPlaying || isPaused) return;

    // Never export while a loop is armed - the fallback recording would repeat forever
    if (isLoopMode) {
      showToastNotification(t('messages.mp3ExitLoopFirst') || 'Exit loop mode before exporting MP3');
      return;
    }

    // Check authentication up front so we never arm the recorder for a
    // playback that a sign-in dialog is about to block
    if (!requireAuth('mp3_export')) {
      return;
    }

    // Fast path: offline render of the already-prepared note events
    try {
      const mutedParts = audioHook.mutedPartsRef.current;
      const events = noteEventsRef.current.filter(event =>
        !mutedParts.has(String(event.partId)) && !mutedParts.has(event.partId)
      );
      if (events.length === 0) {
        showToastNotification(t('messages.noNotes') || 'No playable notes');
        return;
      }

      setIsRenderingMp3(true);
      showToastNotification(t('messages.renderingMp3') || 'Rendering MP3…');

      // Snapshot the exact per-part volumes live playback is using
      const partVolumesDb = {};
      Object.entries(partSynthsRef.current).forEach(([partId, synth]) => {
        if (synth && synth.volume && typeof synth.volume.value === 'number') {
          partVolumesDb[partId] = synth.volume.value;
        }
      });

      const { renderEventsToPcm } = await import('../utils/offlineMp3Renderer');
      const pcm = await renderEventsToPcm({
        events,
        partVolumesDb,
        synthConfig: { ...audioHook.globalSynthConfigRef.current }
      });

      // Encoding failures are terminal - a real-time re-recording would hit
      // the same encoder, so don't fall through to the fallback for those
      try {
        await encodeAndDownloadMp3(pcm);
      } catch (encodeError) {
        console.error('MP3 encoding error:', encodeError);
        showToastNotification(t('messages.mp3Failed') || '✗ MP3 export failed');
      }
      return;
    } catch (error) {
      console.warn('Offline MP3 render failed, falling back to real-time recording:', error);
    } finally {
      setIsRenderingMp3(false);
    }

    // Fallback: record one real-time playback
    try {
      const { createMp3Recorder } = await import('../utils/mp3Recorder');
      mp3PlaybackStartedRef.current = false;
      mp3RecorderRef.current = await createMp3Recorder({
        // Hard safety ceiling reached - force-stop playback; the effect below finalizes
        onLimitReached: () => handleStop()
      });
      setIsRecordingMp3(true);
      showToastNotification(t('messages.recordingMp3') || 'Recording… the MP3 will download when playback ends');
      // No count-off here - it would be recorded into the MP3
      await handlePlay({ countOff: false });

      // Abort cleanly if playback never actually started (e.g. no playable notes)
      setTimeout(() => {
        if (mp3RecorderRef.current && !mp3PlaybackStartedRef.current) {
          mp3RecorderRef.current.stop();
          mp3RecorderRef.current = null;
          setIsRecordingMp3(false);
        }
      }, 8000);
    } catch (error) {
      console.error('MP3 export error:', error);
      if (mp3RecorderRef.current) {
        mp3RecorderRef.current.stop();
        mp3RecorderRef.current = null;
      }
      setIsRecordingMp3(false);
      showToastNotification(t('messages.mp3Failed') || '✗ MP3 export failed');
    }
  }, [hasLoadedFile, isRecordingMp3, isRenderingMp3, isPlaying, isPaused, isLoopMode, requireAuth, audioHook, noteEventsRef, partSynthsRef, encodeAndDownloadMp3, handlePlay, handleStop, showToastNotification, t]);

  // Finalize the MP3 when the recorded playback ends (naturally, via Stop, or via Pause)
  useEffect(() => {
    if (!isRecordingMp3) return;

    if (isPlaying) {
      mp3PlaybackStartedRef.current = true;
      return;
    }

    // Playback hasn't actually started yet (handlePlay is still spinning up)
    if (!mp3PlaybackStartedRef.current) return;

    const finalize = async () => {
      const recorder = mp3RecorderRef.current;
      mp3RecorderRef.current = null;
      setIsRecordingMp3(false);
      if (!recorder) return;

      try {
        const pcm = recorder.stop();
        if (pcm.totalSamples === 0) return;
        await encodeAndDownloadMp3(pcm);
      } catch (error) {
        console.error('MP3 encoding error:', error);
        showToastNotification(t('messages.mp3Failed') || '✗ MP3 export failed');
      }
    };

    finalize();
  }, [isPlaying, isRecordingMp3, encodeAndDownloadMp3, showToastNotification, t]);

  // Audio latency adjustment handler
  const handleLatencyChange = useCallback((newLatency) => {
    setAudioLatency(newLatency);
    localStorage.setItem('musicxml-audio-latency', newLatency.toString());
  }, []);


  const handleExitLoopMode = useCallback(() => {
    
    // Exit loop mode in audio hook
    exitLoopMode();
    
    setIsLoopMode(false);
    setLoopBoundaries(null);
    
    if (hasLoadedFileRef.current) {
      const originalBPM = getOriginalBPM();
      setStatus(t('playback.normalMode', { bpm: originalBPM }));
    }
  }, [getOriginalBPM, exitLoopMode, t]);


  // Settings-related functions
  const getCurrentFileContent = useCallback(() => {
    return currentFileContent;
  }, [currentFileContent]);

  const loadMusicXMLFromContent = useCallback(async (content, filename, isSettingsLoad = false) => {
    // Perform comprehensive cleanup before loading
    performFullCleanup();
    
    setIsLoading(true);
    setStatus(isSettingsLoad ? t('messages.loadingPreset', { preset: filename }) : 'Loading from preset...');
    
    // Small delay to ensure loading indicator is visible
    await new Promise(resolve => setTimeout(resolve, 50));
    
    setIsInitialState(false);

    try {
      content = normalizeClosedScoreSATB(content).content;
      setCurrentFileContent(content);
      setFileName(filename);
      window._currentMusicXMLContent = content; // Make available globally for repeat analysis
      
      // Clear the file input since we're loading programmatically
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      const contentWithMetadata = await injectMetadataTitle(content, filename);
      await osmdRef.current.load(contentWithMetadata);

      // Set zoom before rendering to ensure mobile gets correct initial size
      // But only for direct file loads, not settings loads (which will apply their own zoom)
      if (!isSettingsLoad && osmdRef.current.zoom !== zoom) {
        osmdRef.current.zoom = zoom;
      }

      // Make container visible BEFORE rendering so OSMD can calculate proper dimensions
      // This fixes the blank canvas issue on initial load
      setHasLoadedFile(true);
      hasLoadedFileRef.current = true;

      // Wait for React to update the DOM and browser to layout the container
      // Use multiple frames and a setTimeout to ensure layout is complete on slower devices
      await new Promise(resolve => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setTimeout(resolve, 50);
          });
        });
      });

      // Function to handle render completion
      const handleRenderComplete = async () => {
        // Suppress SVG rendering errors during initial render
        const originalConsoleError = console.error;
        const originalConsoleWarn = console.warn;
        const suppressErrors = (msg, ...args) => {
          if (typeof msg === 'string' && (
            msg.includes('attribute width: A negative value') ||
            msg.includes('attribute height: A negative value') ||
            msg.includes('negative value is not valid')
          )) {
            return; // Suppress SVG errors
          }
          originalConsoleError(msg, ...args);
        };
        
        console.error = suppressErrors;
        console.warn = suppressErrors;
        
        try {
          await osmdRef.current.render();
          return true;
        } finally {
          console.error = originalConsoleError;
          console.warn = originalConsoleWarn;
        }
      };
      
      // Initial render
      await handleRenderComplete();

      // Force OSMD to recalculate dimensions by triggering a resize event
      // This ensures correct rendering even if initial dimensions were wrong
      window.dispatchEvent(new Event('resize'));

      // Initialize measure highlighter after rendering
      setTimeout(() => {
        if (scoreDisplayRef.current) {
          const container = scoreDisplayRef.current.getContainer();
          if (container) {
            if (measureHighlighterRef.current) measureHighlighterRef.current.destroy();
            measureHighlighterRef.current = new MeasureHighlighter(
              osmdRef.current, 
              container,
              (newRange) => {
                setSelectedRange(newRange);
              },
              handleMeasureJump,
              () => isMousePlayMode,
              playClickedNote,
              exitMousePlayMode
            );
            // Initialize with current part visibility state
            measureHighlighterRef.current.updatePartVisibility(partControls);
          }
        }
      }, 100);

      // Initialize note highlighter
      noteHighlighterRef.current = new NoteHighlighter(osmdRef.current);
      
      // Set audio references for highlighting colors
      noteHighlighterRef.current.setAudioReferences(getPartVolume, audioHook.mutedPartsRef);
      
      // Set part control references for divisi highlighting
      noteHighlighterRef.current.setPartControlReferences(
        () => scoreParser.partControlsRef.current, 
        scoreParser.partToInstrumentMapRef
      );

      // Validate the loaded score
      if (!osmdRef.current.Sheet?.Instruments || osmdRef.current.Sheet.Instruments.length === 0) {
        throw new Error(`No distinct instrument parts found in ${filename}`);
      }

      // Set up completion callback
      osmdRef.current._onComplete = () => {
        setShowScore(true);
        setIsLoading(false);
      };

      // Mark if this is a settings load
      osmdRef.current._isSettingsLoad = isSettingsLoad;
      
      // Set up render completion callback for settings loads
      if (isSettingsLoad) {
        osmdRef.current._onRenderComplete = null; // Will be set by settings application
      }

      createPartControls(osmdRef.current);

      // For regular file loads (not from settings), complete immediately
      // Settings loading will be handled by the visibility sync system
      if (!isSettingsLoad) {
        setTimeout(() => {
          if (osmdRef.current._onComplete) {
            osmdRef.current._onComplete();
            osmdRef.current._onComplete = null;
          }
        }, 100);
      }

      updateGlobalSynthSettings();
      setStatus(`Loaded: ${filename}. Preparing for playback...`);

      // iOS Chrome timeout wrapper for preparePlayback
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      if (isMobile) {
        try {
          await Promise.race([
            preparePlayback(osmdRef.current, audioHook),
            new Promise((resolve, reject) => {
              setTimeout(() => {
                resolve();
              }, 3000); // 3 second timeout for mobile audio prep
            })
          ]);
        } catch (error) {
          // Silent error handling for mobile
        }
      } else {
        await preparePlayback(osmdRef.current, audioHook);
      }

      // Auto-detect swing from MusicXML tags (skip if loading from settings)
      if (!isSettingsLoad) {
        const swingInfo = detectSwing(osmdRef.current);
        setDetectedSwingInfo(swingInfo);
        if (swingInfo.hasSwing && !isSwingMode) {
          const timeSignatureAllowsSwing = getTimeSignatureAllowsSwing();
          if (timeSignatureAllowsSwing) {
            setIsSwingMode(true);
            // If file has a ratio and we're in auto mode, use it
            if (swingInfo.swingRatio && swingRatioSource === 'auto') {
              setSwingRatio(swingInfo.swingRatio);
            }
            // Re-run preparePlayback with swing enabled
            await preparePlayback(osmdRef.current, audioHook);
          }
        }
      }

      if (noteEventsRef.current.length > 0) {
        const originalBPM = getOriginalBPM();
        setStatus(`Ready to play: ${filename} (Original tempo: ${originalBPM} BPM)`);
        hasLoadedFileRef.current = true;
        setHasLoadedFile(true);
        setShowScore(true); // Show the score after successful load
      } else {
        // iOS Chrome fallback - assume file is playable even without note events
        if (isMobile && osmdRef.current) {
          setStatus(`Ready to play: ${filename} (iOS Chrome mode)`);
          hasLoadedFileRef.current = true;
          setHasLoadedFile(true);
          setShowScore(true);
        } else {
          setStatus(`No playable notes found for ${filename}.`);
        }
      }

      // For non-settings loading, clear loading state immediately
      // For settings loading, let the caller clear loading state after settings are applied
      if (!isSettingsLoad) {
        setIsLoading(false);
      } else {
        // Add mobile fallback timeout for settings loads
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        if (isMobile) {
          setTimeout(() => {
            if (isLoading) {
              setIsLoading(false);
              setStatus(`Loaded: ${filename} (mobile timeout)`);
            }
          }, 3000); // 3 second mobile fallback
        }
      }
    } catch (error) {
      setStatus(`Error: ${filename} failed to load - ${error.message}`);
      setIsLoading(false);
      hasLoadedFileRef.current = false;
      setHasLoadedFile(false);
    }
  }, [osmdRef, scoreDisplayRef, updateGlobalSynthSettings, preparePlayback, audioHook, getOriginalBPM, noteEventsRef, scoreParser, performFullCleanup, t, injectMetadataTitle]);

  // Set an absolute tempo from the tempo field; applies live during playback
  const handleTempoSet = useCallback((targetBPM) => {
    const originalBPM = getOriginalBPM();
    if (!originalBPM || !Number.isFinite(targetBPM)) return;
    const currentBPM = Math.round(originalBPM * playbackSpeed);
    const newBPM = Math.max(20, Math.min(400, Math.round(targetBPM)));
    if (newBPM === currentBPM) return;
    setPlaybackSpeed(newBPM / originalBPM);
    if (isPlaying || isPaused) {
      // Live change: rescales already-scheduled events via the Transport BPM.
      // When stopped, the speed-change effect below re-prepares playback instead.
      applyLiveTempo(newBPM);
    }
  }, [getOriginalBPM, playbackSpeed, setPlaybackSpeed, isPlaying, isPaused, applyLiveTempo]);

  // Adjust tempo in BPM steps (the +/- buttons)
  const handleTempoAdjust = useCallback((deltaBPM) => {
    const originalBPM = getOriginalBPM();
    if (!originalBPM) return;
    handleTempoSet(Math.round(originalBPM * playbackSpeed) + deltaBPM);
  }, [getOriginalBPM, playbackSpeed, handleTempoSet]);

  // Effect to handle speed changes - re-prepare playback when speed changes
  useEffect(() => {
    if (!hasLoadedFileRef.current || isPlaying || isPaused || isLoopMode) return;
    
    // Only re-prepare if we have a loaded file, we're not playing, and not in loop mode
    if (osmdRef.current && noteEventsRef.current.length > 0) {
      preparePlayback(osmdRef.current, audioHook)
        .then(() => {
          // Tempo changed - no status message needed
        })
        .catch(error => {
          setStatus(t('messages.errorTempo', { error: error.message }));
        });
    }
  }, [playbackSpeed, preparePlayback, audioHook, isPlaying, isPaused, getOriginalBPM, isLoopMode]); // Removed t to prevent language change re-renders

  useEffect(() => {
    if (!hasLoadedFileRef.current) return;
    updateGlobalSynthSettings();
    syncAllPartSynths(true);
    if (isPlaying) {
      setStatus(t('messages.instrumentChanged'));
    }
  }, [instrument, updateGlobalSynthSettings, syncAllPartSynths, isPlaying, hasLoadedFileRef]); // Removed t to prevent language change re-renders

  useEffect(() => {
    if (hasLoadedFileRef.current) {
      synthManager.updateVolume();
    }
  }, [volume, synthManager, hasLoadedFileRef]);

  // Update synth volumes when part volumes change
  useEffect(() => {
    if (hasLoadedFileRef.current) {
      synthManager.updateVolume();
    }
  }, [partVolumes, synthManager, hasLoadedFileRef]);

  // Update selected range state when measure highlighter changes
  useEffect(() => {
    const updateStateInterval = setInterval(() => {
      if (measureHighlighterRef.current) {
        const currentRange = measureHighlighterRef.current.getSelectedRange();
        
        setSelectedRange(prevRange => {
          if (prevRange.start !== currentRange.start || prevRange.end !== currentRange.end) {
            return { ...currentRange };
          }
          return prevRange;
        });
      }
    }, 500); // Reduced frequency since we have immediate callbacks for most changes

    return () => clearInterval(updateStateInterval);
  }, []);

  // Update measure highlighter when part controls are populated
  useEffect(() => {
    if (partControls.length > 0 && measureHighlighterRef.current) {
      measureHighlighterRef.current.updatePartVisibility(partControls);
    }
  }, [partControls]);

  // Sync partControls with parent component
  useEffect(() => {
    if (setParentPartControls) {
      setParentPartControls(partControls);
    }
  }, [partControls, setParentPartControls]);

  // Helper function to calculate visible parts
  const getVisibleParts = () => {
    return partControls.map(p => p.InstrumentId).filter(id => 
      partControls.find(pc => pc.InstrumentId === id)?.visible !== false
    );
  };

  // Helper function to check if all parts are visible
  const areAllPartsVisible = () => {
    return partControls.every(part => part.visible !== false);
  };

  // Handle clicking outside context menu to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (contextMenu.visible) {
        hideContextMenu();
      }
    };

    if (contextMenu.visible) {
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('contextmenu', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('contextmenu', handleClickOutside);
    };
  }, [contextMenu.visible, hideContextMenu]);

  // Cleanup cursor events on unmount
  useEffect(() => {
    return () => {
      scheduledCursorEventIdsRef.current.forEach(id => Tone.Transport.clear(id));
      scheduledCursorEventIdsRef.current = [];
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (noteHighlighterRef.current) {
        noteHighlighterRef.current.clearAllHighlights();
      }
      if (measureHighlighterRef.current) {
        measureHighlighterRef.current.destroy();
      }
    };
  }, []);

  // iOS Chrome fallback - allow play even without note events if file is loaded
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const canPlay = hasLoadedFile && (noteEventsRef.current.length > 0 || isMobile) && !isPlaying;
  const canPause = isPlaying;
  const canStop = isPlaying || isPaused;
  
  // Helper to determine if we're in confidence mode
  const isConfidenceMode = audioHook.overrideVolumesEnabled && selectedPart !== null;
  
  // Helper to determine if range selection should be disabled (only during normal playback, not confidence mode)
  const shouldDisableRangeSelection = isPlaying && !isConfidenceMode;

  // Reset settings to defaults but keep loaded file
  const handleResetSettings = useCallback(() => {
    if (!hasLoadedFile) return;
    
    // Reset all part volumes to 100%
    partControls.forEach(part => {
      setPartVolume(part.id, 100);
    });
    
    // Reset all part visibility to true
    const visibilityUpdates = partControls.map(part => ({ id: part.id, visible: true }));
    handlePartVisibilityBatchWrapper(visibilityUpdates);
    
    // Reset all upper/lower voice settings to true
    const newPartControls = partControls.map(part => ({
      ...part,
      checked: true,
      visible: true,
      upperVoice: true,
      lowerVoice: true
    }));
    setPartControls(newPartControls);
    
    // Reset playback settings
    setPlaybackSpeed(1.0);
    setVolume(0); // Master volume in dB
    setZoom(1.0);
    setAutoScroll(true);
    setLookaheadBeats(0);
    setIsSwingMode(false);
    setSelectedPart(null);
    setConfidence(80);
    if (transposeSteps !== 0) {
      handleTransposeChange(0); // Also reverts notation + audio to original key
    }
    
    // Clear override volumes
    audioHook.setOverrideVolumesEnabled(false);
    
    // Show confirmation
    showToastNotification(t('messages.resetComplete') || '✓ Settings reset to defaults');
  }, [hasLoadedFile, partControls, setPartVolume, handlePartVisibilityBatchWrapper, setPartControls, setPlaybackSpeed, setVolume, setZoom, setAutoScroll, setLookaheadBeats, setIsSwingMode, setSelectedPart, setConfidence, transposeSteps, handleTransposeChange, audioHook, showToastNotification, t]);

  // Prepare data for settings save/load
  const playerState = {
    playbackSpeed,
    volume,
    instrument,
    audioLatency,
    partControls,
    partVolumes,
    zoom,
    autoScroll,
    lookaheadBeats,
    fileName,
    hasLoadedFile,
    selectedPart, // Save which part is "me" for confidence mode
    confidence, // Save the confidence level
    isSwingMode, // Save the swing mode state
    transposeSteps // Save the key transposition
    // Note: selectedRange and isLoopMode are excluded as they are temporary playback state, not persistent settings
  };
  
  // Debug logging for settings
  //console.log('Current playerState for settings:', { selectedPart, confidence });

  const playerActions = {
    setPlaybackSpeed,
    setVolume,
    setInstrument,
    setAudioLatency: (latency) => {
      setAudioLatency(latency);
      localStorage.setItem('musicxml-audio-latency', latency.toString());
    },
    setIsLoopMode,
    setSelectedRange,
    setPartVolume,
    setZoom,
    setAutoScroll,
    setLookaheadBeats,
    setSelectedPart, // Add setter for confidence mode "me" selection
    setConfidence, // Add setter for confidence level
    setIsSwingMode, // Add setter for swing mode
    setSwingRatio, // Add setter for swing ratio
    setSwingRatioSource, // Add setter for swing ratio source
    setTransposeSteps: handleTransposeChange, // Applies notation + audio transposition
    applyConfidenceToOverrides: audioHook.applyConfidenceToOverrides, // Add confidence application function
    handlePartToggle: handlePartToggleWrapper,
    handlePartVisibilityToggle: handlePartVisibilityToggleWrapper,
    onPartVisibilityBatch: handlePartVisibilityBatchWrapper,
    handleUpperVoiceToggle: (instrumentId, enabled) => handleUpperVoiceToggle(instrumentId, enabled, audioHook),
    handleLowerVoiceToggle: (instrumentId, enabled) => handleLowerVoiceToggle(instrumentId, enabled, audioHook),
    syncVisibilityWithUI: syncVisibilityWithUIWrapper,
    measureHighlighterRef,
    setCurrentPresetName,
    setIsLoading,
    osmdRef,
    handleResetSettings
  };

  // Handle loading recent presets
  const handleLoadRecentPreset = useCallback(async (presetName) => {
    if (!user?.id) {
      setStatus('Must be signed in to load presets');
      return;
    }

    try {
      setIsLoading(true);
      const { SettingsManager } = await import('../utils/settingsManager');
      const settings = await SettingsManager.loadPreset(presetName, user.id);
      
      if (settings) {
        // Touch the preset so the song counts as recently practised even if
        // nothing is changed this session (the recents list sorts by savedAt).
        SettingsManager.savePreset(presetName, settings, user.id).catch(() => {});
        setStatus(t('messages.loadingPreset', { preset: presetName }));
        
        // First, load the MusicXML file if it exists
        if (settings.fileName && loadMusicXMLFromContent) {
          try {
            // Download XML content from Vercel Blob storage using filename
            const xmlContent = await SettingsManager.downloadXMLFromBlob(settings.fileName);
            
            // iOS Chrome timeout wrapper
            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            if (isMobile) {
              await Promise.race([
                loadMusicXMLFromContent(xmlContent, settings.fileName, true),
                new Promise((resolve) => {
                  setTimeout(() => {
                    resolve();
                  }, 2000); // 2 second timeout for mobile
                })
              ]);
            } else {
              await loadMusicXMLFromContent(xmlContent, settings.fileName, true);
            }
            await new Promise(resolve => setTimeout(resolve, 300));
          } catch (error) {
            console.error(`Failed to load XML file from blob storage: ${settings.fileName}`, error);
            setStatus(`Error: Could not load music file "${settings.fileName}" from cloud storage. ${error.message}`);
          }
        } else if (settings.fileContent && loadMusicXMLFromContent) {
          // Legacy support for presets that still have embedded content
          await loadMusicXMLFromContent(settings.fileContent, settings.fileName, true);
          await new Promise(resolve => setTimeout(resolve, 300));
        }
        
        // Start batch mode for settings loading to prevent multiple renders
        const renderCoordinator = scoreDisplayRef.current?.getRenderCoordinator?.();
        if (renderCoordinator) {
          renderCoordinator.startBatch();
          
          // Set up completion callback for when batch finishes
          renderCoordinator.onRenderComplete = () => {
            setIsLoading(false);
            setStatus(t('messages.loaded', { preset: presetName }) + (settings.fileName ? ` (${settings.fileName})` : ''));
          };
        } else {
          // Fallback for legacy callback
          if (osmdRef.current) {
            osmdRef.current._onRenderComplete = () => {
              setIsLoading(false);
              setStatus(t('messages.loaded', { preset: presetName }) + (settings.fileName ? ` (${settings.fileName})` : ''));
            };
          }
        }
        
        // Then apply all the settings (in batch mode if available)
        const success = SettingsManager.applySettings(settings, playerActions);
        
        // End batch mode to trigger single render
        if (renderCoordinator) {
          renderCoordinator.endBatch();
        }
        
        if (success) {
          setCurrentPresetName(presetName);
          
          // If no render coordinator is available, use legacy fallback
          if (!renderCoordinator) {
            // If no render callback was triggered (e.g., no zoom change), clear loading manually
            if (!osmdRef.current?._onRenderComplete) {
              setTimeout(() => {
                setIsLoading(false);
                setStatus(t('messages.loaded', { preset: presetName }) + (settings.fileName ? ` (${settings.fileName})` : ''));
              }, 100); // Minimal delay for UI updates
            }
          }
        } else {
          // Clear any pending callback and loading state on error
          if (renderCoordinator) {
            renderCoordinator.onRenderComplete = null;
          }
          if (osmdRef.current) {
            osmdRef.current._onRenderComplete = null;
          }
          setIsLoading(false);
          setStatus(t('messages.errorLoading'));
        }
      } else {
        setIsLoading(false);
        setStatus(t('messages.errorLoading'));
      }
    } catch (error) {
      setIsLoading(false);
      setStatus(t('messages.errorLoading'));
    }
  }, [loadMusicXMLFromContent, playerActions, t, user?.id]);

  const shellActive = hasLoadedFile || isLoading;
  const practiceControls = {
    title: osmdRef.current?.Sheet?.TitleString || fileName || '',
    fileName,
    status,
    isLoading,
    onClose: performFullCleanup,
    onSongSelect: (content, filename) => {
      handleFileSelectionWithPresetCheck(filename, () => handleCloudFileLoad(content, filename));
    },
    onLocalFileLoad: (file) => {
      const syntheticEvent = { target: { files: [file] } };
      handleFileSelectionWithPresetCheck(file.name, () => handleFileUpload(syntheticEvent));
    },
    onRequireSignIn: () => setShowSignInDialog(true),
    getOsmd: () => scoreDisplayRef.current?.getOsmd?.(),
    getRenderCoordinator: () => scoreDisplayRef.current?.getRenderCoordinator?.(),
    // lyrics overlay: the note timeline and where playback is on it
    getNotesWithTiming: () => scoreParser.allNotesWithTimingRef.current,
    addPlayheadListener,
    // lyrics magnifier: dragging the strip moves the playhead to a timeline time
    onSeek: (time) => {
      if (isPlaying) {
        autoScrollerRef.current?.reset(); // so it follows a jump back too
        jumpToTime(time, true);
      } else if (isPaused) {
        audioHook.setPausePosition(time);
        emitPlayhead(time);
      }
    },
    setScoreTapsDisabled: (disabled) => measureHighlighterRef.current?.setTapsDisabled(disabled),
    // hover-to-hear (Mouse Play), exposed in the desktop panel
    hoverPlay: isMousePlayMode,
    setHoverPlay: setIsMousePlayMode,
    wholeChord: isWholeChordMode,
    setWholeChord: setIsWholeChordMode,
    // transport
    isPlaying,
    isPaused,
    canPlay,
    canPause,
    canStop,
    onPlay: handlePlay,
    onPause: handlePause,
    onStop: handleStop,
    // tempo
    tempoBPM: hasLoadedFile && getOriginalBPM() ? Math.round(getOriginalBPM() * playbackSpeed) : null,
    onTempoAdjust: handleTempoAdjust,
    onTempoSet: handleTempoSet,
    metronomeEnabled,
    setMetronomeEnabled,
    // "me" part and blend
    partControls,
    selectedPart,
    openMePartDialog: () => setShowMePartDialog(true),
    showMePartDialog,
    setShowMePartDialog,
    onMePartSelect: handleMePartDialogSelect,
    confidence,
    setConfidence,
    audioHook,
    // display
    zoom,
    setZoom,
    setVisibilityBatch: handlePartVisibilityBatchWrapper,
    // section / loop
    selectedRange,
    isLoopMode,
    shouldDisableRangeSelection,
    onEnterLoopMode: handleEnterLoopMode,
    onExitLoopMode: handleExitLoopMode,
    onClearSelection: handleClearSelection,
    // score features the desktop shell keeps (tips overlay, right-click menu)
    scoreDisplayRef,
    getMeasureHighlighter: () => measureHighlighterRef.current,
    showScore,
    tipsVisible,
    tipAvailability,
    areAllPartsVisible,
    getVisibleParts,
    isInTipPlacementMode,
    handleTipsLoaded,
    handleContextMenu,
    contextMenu,
    handleToggleTips,
    handleEditTip,
    handleDeleteTip,
    handleCreateTip,
    respectRepeats,
    setRespectRepeats,
    // sound
    transposeSteps,
    onTransposeChange: handleTransposeChange,
    isSwingMode,
    setIsSwingMode,
    swingRatio,
    setSwingRatio,
    // song actions
    onExportPdf: handleExportPdf,
    onExportMp3: handleExportMp3,
    isGeneratingPdf,
    isRecordingMp3,
    isRenderingMp3,
    onResetSettings: handleResetSettings,
    onShowTutorial: () => setShowVideoTutorial(true),
    showToast: showToastNotification,
    // presets
    playerState,
    playerActions,
    currentPresetName,
    user,
    // More sheet
    hasLoadedFile,
    instrument,
    setInstrument,
    velocity,
    setVelocity,
    volume,
    setVolume,
    onPartToggle: handlePartToggleWrapper,
    onPartVisibilityToggle: handlePartVisibilityToggleWrapper,
    onUpperVoiceToggle: (instrumentId, enabled) => handleUpperVoiceToggle(instrumentId, enabled, audioHook),
    onLowerVoiceToggle: (instrumentId, enabled) => handleLowerVoiceToggle(instrumentId, enabled, audioHook),
    osmdRef,
    getPartVolume,
    setPartVolume,
    onSelectedPartChange: handleSelectedPartChange,
    autoScroll,
    setAutoScroll,
    lookaheadBeats,
    setLookaheadBeats,
    audioLatency,
    setAudioLatency: handleLatencyChange,
    getCurrentFileContent,
    loadMusicXMLFromContent,
    setStatus,
    onToggleRespectRepeats: handleToggleRespectRepeats,
    tipRefreshTrigger,
    songVideos,
    onVideoStart: handleVideoStart,
    // Swing: same steps as the desktop checkbox / ratio select
    swingAllowed: hasLoadedFile && getTimeSignatureAllowsSwing(),
    swingRatioSource,
    detectedSwingInfo,
    setSwingEnabled: (enabled) => {
      setIsSwingMode(enabled);
      if (hasLoadedFile && scoreParser.allNotesWithTimingRef.current.length > 0) {
        preparePlayback(osmdRef.current, audioHook);
        if (isLoopMode && loopBoundaries) handleEnterLoopMode();
      }
    },
    setSwingRatioSource: (source) => {
      setSwingRatioSource(source);
      const fileHasSwingRatio = detectedSwingInfo?.swingRatio != null;
      const ratios = { '2:1': 2 / 3, '3:2': 3 / 5, '5:3': 5 / 8 };
      const autoRatio = fileHasSwingRatio ? detectedSwingInfo.swingRatio : 5 / 8;
      setSwingRatio(source === 'auto' ? autoRatio : (ratios[source] ?? 5 / 8));
      if (isSwingMode && hasLoadedFile && scoreParser.allNotesWithTimingRef.current.length > 0) {
        preparePlayback(osmdRef.current, audioHook);
        if (isLoopMode && loopBoundaries) handleEnterLoopMode();
      }
    }
  };

  const scoreArea = (
    <>
      {isLoading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          backdropFilter: 'blur(3px)',
          minHeight: '400px'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '20px'
          }}>
            {/* Loading Spinner */}
            <div style={{
              width: '50px',
              height: '50px',
              border: '4px solid rgba(255, 255, 255, 0.3)',
              borderTop: '4px solid #4fc3f7',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            
            {/* Loading Text */}
            <div style={{
              fontSize: '18px',
              fontWeight: '500',
              color: '#ffffff',
              textAlign: 'center',
              textShadow: '0 1px 3px rgba(0, 0, 0, 0.5)'
            }}>
              {status}
            </div>
          </div>
        </div>
      )}

      <ScoreDisplay
        ref={scoreDisplayRef}
        onOsmdReady={handleOsmdReady}
        zoom={zoom}
        isMousePlayMode={isMousePlayMode}
        currentFileContent={currentFileContent}
        onPlayNote={playClickedNote}
        partControls={partControls}
        cue={scoreCue}
      />
    </>
  );

  if (!isPhone) {
    return (
      <div className="musicxml-player-container desktop-practice-root">
        <SongGrid
          hasLoadedSong={shellActive}
          onFileLoad={practiceControls.onSongSelect}
          onLocalFileLoad={practiceControls.onLocalFileLoad}
          onRequireSignIn={practiceControls.onRequireSignIn}
        />

        <DesktopPracticeShell active={shellActive} controls={practiceControls}>
          {scoreArea}
        </DesktopPracticeShell>

        {user && user.id && (
          <OnScoreTipDialog
            isOpen={showCreateTipDialog}
            onClose={handleCreateTipClose}
            onCreateTip={handleCreateTipSubmit}
            musicxmlId={fileName}
            osmdInstance={osmdRef.current}
            visibleParts={getVisibleParts()}
            initialPosition={createTipPosition}
          />
        )}
        {user && user.id && (
          <OnScoreTipDialog
            isOpen={showEditTipDialog}
            onClose={handleEditTipClose}
            onCreateTip={handleEditTipSubmit}
            editingTip={editingTip}
            musicxmlId={fileName}
            osmdInstance={osmdRef.current}
            visibleParts={getVisibleParts()}
          />
        )}

        <VideoPlayerModal
          isOpen={showVideoTutorial}
          onClose={() => setShowVideoTutorial(false)}
          videoUrl={tutorialVideoURL}
          title="MusicXML Player Tutorial"
        />
        <FeatureSignInDialog
          isOpen={showSignInDialog}
          onClose={() => setShowSignInDialog(false)}
          featureName={t('app.title')}
        />
        {showToast && (
          <div className="mps-toast" data-error={toastMessage.includes('✗') ? 'true' : 'false'}>
            {toastMessage}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="musicxml-player-container mobile-practice-root">
      {/* Song cards, as on desktop; on a phone a tap goes straight into practice */}
      <SongGrid
        hasLoadedSong={shellActive}
        tapToPractice
        onFileLoad={practiceControls.onSongSelect}
        onLocalFileLoad={practiceControls.onLocalFileLoad}
        onRequireSignIn={practiceControls.onRequireSignIn}
      />

      <MobilePracticeShell active={shellActive} controls={practiceControls}>
        {scoreArea}
      </MobilePracticeShell>

      <VideoPlayerModal
        isOpen={showVideoTutorial}
        onClose={() => setShowVideoTutorial(false)}
        videoUrl={tutorialVideoURL}
        title="MusicXML Player Tutorial"
      />

      <FeatureSignInDialog
        isOpen={showSignInDialog}
        onClose={() => setShowSignInDialog(false)}
        featureName={t('app.title')}
      />

      {showToast && (
        <div className="mps-toast" data-error={toastMessage.includes('✗') ? 'true' : 'false'}>
          {toastMessage}
        </div>
      )}
    </div>
  );
};

export default MusicXMLPlayer;